// The one-bit screen (studio/ART-BIBLE.md, reference docs/ui-directions/02-one-bit-events.html).
// Draws the whole 640x360 game into one canvas: menu bar, the door with reeded glass, the chat window and the
// "This shift" window. The real DOM controls stay in the page (for keyboard, screen readers and ui.js) as
// transparent hit areas that this renderer places over whatever it drew for them.
import { createBits, canvasGlyphs, dz, T, W, H } from './bits.js';
import { silhouetteOf, drawGlass, knockerHands, GLASS, archAt, seedOf } from './visitors.js';
import { knockerSheet, clearOf } from './knocker.js';
import { creepFrame, leanOf, lastTenOn, punchView, stampArt, STAMP_FROM } from './red.js';
import { eyeTrack, handOn, canHover } from './eyes.js';
import { glassState, revealLamp } from './reactions.js';
import { beastState, isBeast } from './beasts.js';
import { litState, litOf, litPlate } from './neighbours-lit.js';
import { noWords } from './words.js';
import { phoneFrame, plateY, TRAY_BOTTOM, verdictButtons, mailFit, mailFitDesk, tonightLines, PHONE } from './phone.js';
import { roundSeconds, barTicks } from './round-clock.js';
import { stepsShown, SUM_FIRST_MS, SUM_STEP_MS } from './sum.js';
import { firstMinuteChrome, RULE_MIN_PX, RULE_MAX_PX } from './first-minute.js';
import { coinLabels, COPY_LABEL, COPIED_LABEL } from './coin.js';
import { OFF_SHORT, OFF_TAB, OFF_TAB_PHONE, shortRoomName, START_OFF_TITLE, START_OFF_LINE, START_OFF_WHY, START_OFF_ROOM } from '../game/teaser.js';
import { tallyGroups, streakText, SMEAR_MS } from './tally.js';
import { MARK } from './brand-bits.js';
import { mugshot, layoutCard, drawCard } from './reveal-card.js';
import { blitNight, nightSheet, plateFrame, leafArt, ART } from './night.js';
import { blitToy, mixGlitch, toySheet, toyPicker, toyFrame } from './toy.js';

export { W, H };
export const FPS = 15;

const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const ease = p => (p < 0 ? 0 : p > 1 ? 1 : 1 - (1 - p) * (1 - p));
const hash = (a, b) => { let h = (a * 374761393 + b * 668265263) | 0; h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };

// desktop layout (logical pixels); the phone layout (phone.js) moves these per frame
const GW = GLASS.w, GH = GLASS.h;                    // reeded glass arch (visitors.js paints it)
const DESK = { DX: 46, DY: 40, DW: 228, DH: 295,     // door leaf
  GX: 70, GY: 56,                                    // glass
  CX: 300, CY: 22, CW: 332, CH: 228 };               // chat window (sprint 3: 326, 24, 304 x 212)
// the x-tag on a verdict: a special keeps its own light, plague green is for an outbreak only, a streak alone is ink on paper
// (a lit knock, neighbours-lit.js, is a bull knock in its own look: its tag wears the look's light)
export const multTagTint = v => (v.special === 'gold' ? T.gold : v.special === 'red' ? T.red : litOf(v.special, v.look) ? T[v.look]
  : isBeast(v.special) ? T[v.special] : v.double ? T.green : T.none);
export const CHAT = { x: DESK.CX, y: DESK.CY, w: DESK.CW, h: DESK.CH };
const KNOCKS = [0.15, 0.6, 1.05];
// studio blind-read shots only: a demo URL with plate=0 leaves the event plate off the glass (never in play: no demo, no flag)
const BLIND = typeof location !== 'undefined' && (() => { const q = new URLSearchParams(location.search); return !!q.get('demo') && q.get('plate') === '0'; })();
const DISCLAIMER = 'Not an investment. The coin can go to zero. Prizes go to the best players of each night shift.';
const SHIFT_POOL_NOTE = "prize pool, paid to each shift's top 10";
const MENU_LABEL = { practice: 'Practice room', story: 'Story', night: 'Night shift (holders)' };
const PHONE_LABEL = { practice: 'Practice', story: 'Story', night: 'Night shift' };

// words: the sentence-text overlay (words.js). Every line read as a sentence goes there, not into the raster;
// the raster keeps the pixel fonts for big numbers, titles, buttons and labels.
// opts.night: the door wears level 2 night colour (night.js; the browser's default door, ui.js); off, the frame is one bit
export function createOneBitScene(canvas, doc = document, words = noWords, opts = {}) {
  let night = !!opts.night;
  const g = canvas.getContext('2d'), glyphs = canvasGlyphs(doc);
  // SW: the canvas width in logical pixels. The layout is 640 wide; a wider window (1366 at 2x is 683) gets more
  // room and menu bar on the right instead of a black strip (scale.js oneBitWidth)
  let SW, SH = H, B, px, get, fill, hline, vline, box, dotted, xbox, xfill, tw, text, wrap, S, img, d32;
  // P: the phone layout (phone.js) is on; PH: its play height (the canvas runs on below it for "This shift")
  let P = false, PH = H, PF = null;
  let chrome = firstMinuteChrome(false); // what the menu bar shows (set each frame: the first minute leaves out the rooms, wallet and links)
  let { DX, DY, DW, DH, GX, GY, CX, CY, CW, CH } = DESK;
  let IX = CX, IY = CY + CH + 6, IW = CW, IH = H - (CY + CH + 6) - 4; // "This shift" window
  function setup(w, h, phone) {
    w = Math.max(phone ? PHONE.minW : W, w | 0);
    if (!phone) h = Math.max(H, h | 0);
    if (w === SW && h === SH && phone === P) return;
    SW = w; SH = h; P = phone; canvas.width = SW; canvas.height = SH;
    if (canvas.style) { canvas.style.width = SW + 'px'; canvas.style.height = P || SH > H ? SH + 'px' : ''; }
    g.imageSmoothingEnabled = false;
    B = createBits(SW, SH, glyphs);
    ({ px, get, fill, hline, vline, box, dotted, xbox, xfill, tw, text, wrap, S } = B);
    img = g.createImageData(SW, SH); d32 = new Uint32Array(img.data.buffer);
    if (!P) ({ DX, DY, DW, DH, GX, GY, CX, CY, CW, CH } = DESK), IX = CX, IY = CY + CH + 6, IW = CW, IH = H - (CY + CH + 6) - 4;
    const jack = doc.getElementById?.('jack');
    if (jack && !P) jack.style.left = jack.style.top = jack.style.visibility = '';
  }
  // desktop: the 640x360 layout, w logical pixels wide
  function resize(w) { setup(w, H, false); }
  // a window taller than the stage (1440x900 at 2x is 450 rows) gets more floor below it, not a black band
  function tall(w, h) { setup(w, h, false); }
  // phone (portrait, under 640 px): w logical pixels wide, playH tall above "This shift" (phone.js phoneSize)
  function phone(w, playH) { PH = playH; setup(w, phoneFrame(Math.max(PHONE.minW, w | 0), playH, 0, false, !nightOff()).h, true); }
  resize(W);
  const $ = id => doc.getElementById(id);

  // ---------- hit areas ----------
  let placed = new Map(), lastPlaced = new Map();
  const tracked = new Set();
  function place(el, x, y, w, h) {
    if (!el) return;
    tracked.add(el);
    placed.set(el, [x, y, w, h]);
    const k = `${x},${y},${w},${h}`;
    if (el.dataset.hit !== k) {
      el.dataset.hit = k;
      Object.assign(el.style, { left: x + 'px', top: y + 'px', width: w + 'px', height: h + 'px' });
    }
  }
  function settleHits() {
    for (const el of tracked) {
      const on = placed.has(el);
      if (el.isConnected === false) { tracked.delete(el); continue; }
      const v = on ? '' : 'hidden';
      if (el.style.visibility !== v) el.style.visibility = v;
    }
    lastPlaced = placed; placed = new Map();
  }

  // ---------- widgets ----------
  // Sprint 12 (roadmap 23): titles, the menu bar, buttons, labels, plates and tags are sentence text (words.js),
  // placed by their vertical centre. The pixel font text() is left for the SOL amount and the countdowns only.
  const lw = (s, role) => Math.ceil(words.measure(s, role));
  function label(s, x, cy, role, v = 0, align = 'left') {
    return words.say(s, x + S.ox, Math.round(cy - words.line(role) / 2), role, v, align, S.clip);
  }
  // a title cut to fit its room, with an ellipsis
  function fit(s, role, max) {
    if (lw(s, role) <= max) return s;
    while (s.length > 1 && lw(s + '…', role) > max) s = s.slice(0, -1);
    return s.trimEnd() + '…';
  }
  const titleOf = (title, w) => fit(title, 'title', w - 52);
  // every window drawn this frame, so the jack-o'-lantern (an element over the canvas) can hide under them
  const framesDrawn = [];
  const JACK_DESK = [7, 318, 22, 18]; // halloween.css: the desktop jack's place and size
  // words.cover, cut to the current clip, so text the clipped art cannot reach stays
  function coverClipped(x, y, w, h) {
    if (S.clip) { const [cx, cy, cw, ch] = S.clip, x1 = Math.max(x, cx), y1 = Math.max(y, cy); w = Math.min(x + w, cx + cw) - x1; h = Math.min(y + h, cy + ch) - y1; x = x1; y = y1; }
    if (w > 0 && h > 0) words.cover(x, y, w, h);
  }
  function windowFrame(x, y, w, h, title) {
    framesDrawn.push([x, y, w + 2, h + 2]);
    fill(x + 2, y + 2, w, h, 0);
    fill(x, y, w, h, 1); box(x, y, w, h, 0);
    for (let j = y + 3; j < y + 14; j += 2) hline(x + 3, j, w - 6, 0);
    hline(x, y + 16, w, 0);
    fill(x + 8, y + 3, 13, 11, 1); box(x + 9, y + 3, 11, 11, 0);
    const t = titleOf(title, w);
    const tW = lw(t, 'title'); fill(Math.round(x + w / 2 - tW / 2 - 6), y + 1, tW + 12, 15, 1);
    label(t, x + w / 2, y + 8.5, 'title', 0, 'center');
  }
  function bubble(x, y, w, h, inverted) {
    const bg = inverted ? 0 : 1;
    fill(x + 1, y, w - 2, h, bg); fill(x, y + 1, w, h - 2, bg);
    if (!inverted) {
      hline(x + 2, y, w - 4); hline(x + 2, y + h - 1, w - 4); vline(x, y + 2, h - 4); vline(x + w - 1, y + 2, h - 4);
      px(x + 1, y + 1, 0); px(x + w - 2, y + 1, 0); px(x + 1, y + h - 2, 0); px(x + w - 2, y + h - 2, 0);
      for (let k = 0; k < 5; k++) { hline(x + 6, y + h - 1 + k, 5 - k, 1); px(x + 5, y + h - 1 + k, 0); px(x + 11 - k, y + h - 1 + k, 0); }
    } else for (let k = 0; k < 4; k++) hline(x + w - 10 + k, y + h - 1 + k, 4 - k, 0);
    return inverted ? 1 : 0;
  }
  // The slip (roadmap 30, data-slip on the chat line): the bubble gets the default button's thick ring (ink, a paper
  // gap, then the bubble; its tail breaks through) and the one-bit pointing hand beside it, aimed at the line. The hand
  // sits on the open side (right of a doorkeeper's view of the visitor, left of the knocker's own line) and is left
  // out when the bubble is too wide for it. Ink and paper only; still, no motion.
  const HAND = [ // pointing left; '#' ink, '.' inside is paper, ' ' clear
    ' ########       ',
    '#........#####  ',
    ' ########.....# ',
    '     #........# ',
    '    #.####....# ',
    '     #........# ',
    '    #.####....# ',
    '     #.......#  ',
    '      #######   ',
  ];
  function slipMark(bx, y, w, h, mine, left, right) {
    fill(bx - 4, y - 4, w + 8, h + 8, 0); fill(bx - 2, y - 2, w + 4, h + 4, 1);
    for (const [a, b] of [[bx - 4, y - 4], [bx + w + 3, y - 4], [bx - 4, y + h + 3], [bx + w + 3, y + h + 3]]) px(a, b, 1);
    const hw = HAND[0].length, hy = y + Math.max(0, Math.round((Math.min(h, 22) - HAND.length) / 2));
    const hx = mine ? bx - 7 - hw : bx + w + 7;
    if (hx < left + 2 || hx + hw > right - 2) return;
    HAND.forEach((row, j) => [...row].forEach((c, i) => { if (c !== ' ') px(hx + (mine ? hw - 1 - i : i), hy + j, c === '#' ? 0 : 1); }));
  }
  // square button; isDefault draws the classic thick ring
  // counted: the filled ink button (the "Voted" look on the pages), never the dotted disabled frame
  function button(el, x, y, w, name, key, isDefault, disabled, counted = false) {
    const h = P ? PHONE.touch : 20;
    if (isDefault && !disabled) {
      fill(x - 4, y - 4, w + 8, h + 8, 0); fill(x - 2, y - 2, w + 4, h + 4, 1);
      for (const [a, b] of [[x - 4, y - 4], [x + w + 3, y - 4], [x - 4, y + h + 3], [x + w + 3, y + h + 3]]) px(a, b, 1);
    }
    if (counted) {
      fill(x, y, w, h, 0); px(x, y, 1); px(x + w - 1, y, 1); px(x, y + h - 1, 1); px(x + w - 1, y + h - 1, 1);
      label(name, x + w / 2, y + h / 2, 'label', 1, 'center');
      return place(el, x, y, w, h);
    }
    fill(x, y, w, h, 1);
    if (disabled) { for (let i = 0; i < w; i += 2) { px(x + i, y, 0); px(x + i, y + h - 1, 0); } for (let j = 0; j < h; j += 2) { px(x, y + j, 0); px(x + w - 1, y + j, 0); } }
    else { box(x, y, w, h, 0); px(x, y, 1); px(x + w - 1, y, 1); px(x, y + h - 1, 1); px(x + w - 1, y + h - 1, 1); }
    // a disabled label stays solid ink (sentence text is never dithered); the dotted frame says it is disabled; a label wider
    // than its button is cut to fit with an ellipsis, so it never runs over the frame
    name = fit(name, 'label', w - 10 - (key ? 18 : 0));
    const lbw = lw(name, 'label'), total = lbw + (key ? 18 : 0), sx0 = Math.round(x + (w - total) / 2), cy = y + h / 2;
    label(name, sx0, cy, 'label', 0);
    if (key) { const kx = sx0 + lbw + 7, ky = Math.round(cy - 6); if (disabled) box(kx, ky, 11, 12, 0); else fill(kx, ky, 11, 12, 0); label(key, kx + 5.5, ky + 6, 'tag', disabled ? 0 : 1, 'center'); }
    place(el, x, y, w, h);
  }
  // role: what is typed is sentence text (words.js), a name as much as a message
  function field(el, x, y, w, h, placeholder, now, role = 'chat') {
    const disabled = el.disabled, focused = doc.activeElement === el, value = el.value;
    fill(x, y, w, h, 1);
    if (disabled) { for (let i = 0; i < w; i += 2) { px(x + i, y, 0); px(x + i, y + h - 1, 0); } vline(x, y, h, 0); vline(x + w - 1, y, h, 0); }
    else { box(x, y, w, h, 0); hline(x + 1, y + 1, w - 2, 0); }
    const prev = S.clip; S.clip = [x + 2, y + 1, w - 4, h - 2];
    let tx = x + 5;
    if (value) {
      const vw = Math.ceil(words.measure(value, role)); if (vw > w - 14) tx = x + w - 9 - vw;
      words.say(value, tx + S.ox, y + Math.round((h - words.line(role)) / 2), role, 0, 'left', S.clip); tx += vw;
    } else { // a placeholder is a sentence: solid ink in the words layer, a 1 px dotted underline marks it as not typed
      const pw = Math.ceil(words.say(placeholder, tx + S.ox, y + Math.round((h - words.line(role)) / 2), role, 0, 'left', S.clip));
      for (let i = 0; i < pw; i += 2) px(tx + i, y + h - 4, 0);
    }
    if (focused && Math.floor(now / 1000 * FPS) % 10 < 6) vline(value ? tx + 1 : x + 4, y + 3, h - 6, 0);
    S.clip = prev;
    place(el, x, y, w, h);
  }
  function zoom(from, to, p) {
    for (let k = 0; k < 4; k++) {
      const q = clamp(p * 1.5 - k * 0.17); if (q <= 0 || q >= 1) continue; const e = ease(q);
      xbox(from[0] + (to[0] - from[0]) * e, from[1] + (to[1] - from[1]) * e, from[2] + (to[2] - from[2]) * e, from[3] + (to[3] - from[3]) * e);
    }
  }
  function heart(x, y, ok) {
    if (ok) { for (let j = 0; j < 4; j++) hline(x + 4 - j, y + j, 1 + j * 2, 0); fill(x, y + 4, 9, 6, 0); fill(x + 3, y + 6, 3, 4, 1); }
    else { box(x, y + 4, 9, 6, 0); for (let i = 0; i < 9; i++) px(x + i, y + 9 - Math.round(i * 5 / 8), 0); for (let j = 0; j < 4; j++) { px(x + 4 - j, y + j, j & 1); px(x + 4 + j, y + j, j & 1); } }
  }

  // chat bubbles remember when they first showed, for the zoom-in
  const seen = new WeakMap();
  let firstDraw = true;

  /* ================= phone layout (phone.js): the parts that differ from the desktop ================= */
  // the menu bar: a menu button (the door icon) pulls down wallet, look, sound and roadmap; the two rooms;
  // the shelter count. One touch row high.
  function phoneMenu(st, flash) {
    const M = PHONE.menuH, menuEl = $('menu'), open = menuEl?.getAttribute('aria-expanded') === 'true';
    fill(0, 0, SW, M, 1); hline(0, M, SW, 0);
    if (open) fill(0, 0, 24, M, 0);
    const v = open ? 1 : 0;
    fill(4, 5, 9, 12, v); fill(6, 7, 5, 4, 1 - v); px(11, 12, 1 - v); // a tiny door
    for (let k = 0; k < 3; k++) hline(15 + k, 10 + k, 5 - 2 * k, v);  // and a pull-down arrow
    if (menuEl) place(menuEl, 0, 0, 24, M);
    for (const id of [...['wallet', 'mute', 'look', 'roadmap', 'leaders', 'logbook', 'stairwell'], 'report', 'coincopy', 'discord']) { const el = $(id); if (el) tracked.add(el); } // hidden while the menu is shut
    if ($('roompick')) tracked.add($('roompick')); // the pull-down's "Pick a room" (phoneMenuDrop)
    let mx = 25;
    const left = st.shelter?.left ?? 10, total = st.shelter?.total ?? 10, count = `${left}/${total}`;
    const rooms = [...doc.querySelectorAll('.rooms button')], long = b => (b.dataset.off ? OFF_TAB_PHONE : PHONE_LABEL[b.dataset.room] ?? b.textContent.trim());
    // a narrow phone (320 px) shortens "Night shift" to "Night" so the shelter count keeps its place
    const roomy = mx + rooms.reduce((s, b) => s + lw(long(b), 'menu') + 11, 0) + lw(count, 'menu') + 8 <= SW;
    const f = flash ? 1 : 0; // the visual beep inverts the bar, so its words flip with it
    for (const b of rooms) {
      if (!chrome.rooms) { tracked.add(b); continue; }
      const name = roomy ? long(b) : shortRoomName(long(b)), on = b.getAttribute('aria-pressed') === 'true', w = lw(name, 'menu') + 10;
      if (on) fill(mx, 0, w, M, 0);
      label(name, mx + 5, M / 2, 'menu', (on ? 1 : 0) ^ f);
      place(b, mx, 0, w, M);
      mx += w + 1;
    }
    // the shared shelter is not shown to a visitor who has not played a door of their own yet (sprint 31: it was already 3/10 when they arrived)
    if (st.houses !== false) {
      const cw = label(count, SW - 5, M / 2, 'menu', f, 'right');
      if (SW - 5 - cw - 13 >= mx) heart(Math.round(SW - 5 - cw - 13), 7, left > 0);
    }
    // the first minute's knock count, in the bar's free room instead of a header row over the glass
    if (st.coach?.firstMinute) {
      const t = `Knock ${st.coach.step} of ${st.coach.of}`, tw = lw(t, 'menu');
      if (st.houses === false) label(t, SW - 5, M / 2, 'menu', f, 'right');
      else if (mx + 5 + tw <= SW - 5 - lw(count, 'menu') - 20) label(t, mx + 5, M / 2, 'menu', f);
    }
    if (flash) xfill(0, 0, SW, M);
  }
  function phoneMenuDrop() {
    const menuEl = $('menu');
    if (menuEl?.getAttribute('aria-expanded') !== 'true') return;
    const T = PHONE.touch, ids = [...['wallet', 'mute', 'look', 'roadmap', 'leaders', 'logbook', 'stairwell'], 'report'].filter(id => chrome[id] !== false && !$(id)?.hidden);
    if (chrome.discord !== false && $('discord')) ids.splice(ids.length - 1, 0, 'discord'); // the Discord link (client/coin.js), before "Report a bug"
    if (chrome.rooms) ids.splice(ids.length - 1, 0, 'roompick'); // Sprint 29 fix (QA B1): the way to the rooms, before "Report a bug"
    const items = ids.map($).filter(Boolean), labels = items.map(el => el.textContent.trim());
    // the $KNOCK field (client/coin.js), a row above Discord: the address with Copy at its end once there is one, else the placeholder
    const coinEl = $('coin'), copyEl = $('coincopy'), mint = coinEl?.dataset.mint, coinAt = chrome.coin !== false && coinEl ? Math.max(0, items.indexOf($('discord'))) : -1;
    const copyW = mint && copyEl ? Math.max(lw(COPY_LABEL, 'menu'), lw(COPIED_LABEL, 'menu')) + 12 : 0, coinTexts = coinLabels(mint);
    if (coinAt >= 0) { items.splice(coinAt, 0, copyW ? copyEl : coinEl); labels.splice(coinAt, 0, ''); }
    const w = Math.min(SW - 8, Math.max(...labels.map(l => lw(l, 'menu')), coinAt >= 0 ? lw(coinTexts[0], 'menu') + copyW : 0) + 28), x = 0, y = PHONE.menuH;
    // the field's words: the longest that fits one line, else the full words on two lines (the placeholder keeps "only from @knockgamefun")
    const room = w - 24 - (copyW ? copyW + 6 : 0), one = coinTexts.find(t => lw(t, 'menu') <= room), two = words.wrap(coinTexts[0], 'menu', room);
    const coinLines = coinAt < 0 ? [] : one === coinTexts[0] || two.length > 2 || two.some(l => lw(l, 'menu') > room) ? [one ?? coinTexts.at(-1)] : two;
    const rowH = i => (i === coinAt ? Math.max(T, Math.ceil(coinLines.length * words.line('menu')) + 8) : T);
    const h = items.reduce((n, _, i) => n + rowH(i), 4);
    words.cover(x, y, w + 3, h + 3);
    fill(x + 2, y + 2, w, h, 0); fill(x, y, w, h, 1); box(x, y, w, h, 0);
    let yy = y + 2;
    items.forEach((el, i) => {
      const rh = rowH(i);
      if (i) dotted(x + 4, yy, w - 8, 0);
      if (i === coinAt) {
        const lh = words.line('menu'), top = yy + rh / 2 - (coinLines.length - 1) * lh / 2;
        coinLines.forEach((l, k) => label(l, x + 12, top + k * lh, 'menu', 0));
        if (copyW) { label(copyEl.textContent.trim(), x + w - 12, yy + rh / 2, 'menu', 0, 'right'); place(copyEl, x + 1, yy, w - 2, rh); } // the whole row copies
        // without an address the row is words to read, not a control
      } else {
        label(labels[i], x + 12, yy + rh / 2, 'menu', 0);
        place(el, x + 1, yy, w - 2, rh);
      }
      yy += rh;
    });
  }
  // "This shift", under the play area: the pool, the notice board, then the ledger, stacked
  function phoneHour(st, clock, startUp) {
    if (nightOff()) return offHour(!startUp);
    const say = (s, x, y, role, v = 0, align = 'left') => words.say(s, x, y, role, v, align);
    const blh = words.line('board'), rlh = words.line('receipt'), T = PHONE.touch;
    windowFrame(IX, IY, IW, IH, st.shiftTitleShort ?? 'This shift');
    let iy = IY + 20;
    const sol = (Number(st.prizePoolLamports ?? 0) / 1e9).toFixed(2);
    text(`${sol} SOL`, IX + 12, iy, 'big', 0);
    if (st.shiftTotal != null) shiftCorner(st.shiftTotal, IX + IW - 8, iy - 3, true);
    iy += 19;
    for (const l of words.wrap(SHIFT_POOL_NOTE, 'line', IW - 24)) { say(l, IX + 12, iy, 'line', 0); iy = Math.round(iy + words.line('line')); }
    if (st.sidesLine) { say(st.sidesLine, IX + 12, iy, 'line', 0); iy = Math.round(iy + words.line('line')); } // both counts in words, as the short title only fits "D 3/5 K 2/5"
    iy += 3; hline(IX + 1, iy, IW - 2, 0);
    const LX0 = IX + 12, LW = IW - 24;
    const rows = [...($('board')?.children ?? [])];
    if (!rows.length) say(($('board')?.textContent ?? '').trim() || 'No scores this shift yet.', LX0, iy + 3, 'board', 0);
    rows.slice(0, 4).forEach((row, i) => {
      const yy = Math.round(iy + 3 + i * blh), me = row.classList.contains('you');
      const [n, p] = [...row.children].map(s => s.textContent);
      if (me) fill(LX0 - 4, yy, LW + 8, Math.round(blh), 0);
      const name = n.length > 18 ? n.slice(0, 17) + '…' : n;
      say(name, LX0, yy, 'board', me ? 1 : 0); say(String(p), LX0 + LW, yy, 'board', me ? 1 : 0, 'right');
    });
    iy = Math.round(iy + 3 + Math.max(1, Math.min(4, rows.length)) * blh + 3); hline(IX + 1, iy, IW - 2, 0);
    let gy = iy + 3;
    label('Payout ledger', LX0, gy + 7, 'title', 0); gy += 15;
    const ledger = [...($('receipt')?.children ?? [])].filter(a => a.tagName === 'A');
    if (!ledger.length) { say(($('receipt')?.textContent ?? '').trim() || 'No payouts yet.', LX0, gy, 'receipt', 0); gy = Math.round(gy + rlh); }
    for (const a of ledger.slice(0, 2)) { // each row is a link to the explorer: one touch row high
      const [l, rr] = [...a.children].map(s => s.textContent), ty = Math.round(gy + (T - rlh) / 2);
      say(l, LX0, ty, 'receipt', 0); say(rr + ' SOL', LX0 + LW, ty, 'receipt', 0, 'right');
      dotted(LX0, gy + T - 1, LW, 0);
      place(a, LX0, gy, LW, T);
      gy += T;
    }
    // the disclaimer is not repeated here: on a phone its strip is fixed to the bottom of the window (onebit.css)
    const foot = IY + IH - 6;
    if (st.prizePct && gy + 2 + rlh <= foot) { say('split', LX0, gy + 2, 'receipt', 0); say(`${st.prizePct}% of fees to prizes`, LX0 + LW, gy + 2, 'receipt', 0, 'right'); }
  }
  // A practice lesson's coach line (game/tutorial.js), in sentence text. Desktop: it takes the place of "This shift"
  // for the round (the disclaimer stays in it). Phone: only a one-row header (the lesson number and Skip) over the
  // top of the glass, above every visitor's eyes and the event plate; the line itself is a box over the chat's
  // messages (coachBox), open until the first question, and a tap on the header opens or shuts it.
  // between: the verdict is up, so the panel says the points are practice (the alert holds Skip lessons then)
  function coachDesk(c, say, between = false) {
    windowFrame(IX, IY, IW, IH, c.firstMinute ? `First knock ${c.step} of ${c.of}` : `Practice lesson ${c.step} of ${c.of}`);
    const lh = words.line('coach'), skip = $('coachskip'), foot = IY + IH - 14; // the disclaimer strip starts here
    const body = between ? "Lessons aren't scored." : c.text;
    const rows = words.wrap(body, 'coach', IW - 24);
    if (c.firstMinute && c.step === 1 && !between) rows.push(...words.wrap(c.startLine, 'coach', IW - 24)); // the core idea, once, over the first knock
    rows.forEach((l, i) => words.say(l, IX + 12, Math.round(IY + 23 + i * lh), 'coach', 0));
    if (!between && skip && !skip.hidden) button(skip, IX + IW - 12 - 124, foot - 28, 124, 'Skip lessons', '', false, false);
    hline(IX + 1, foot, IW - 2, 0);
    say(DISCLAIMER, IX + IW / 2, foot + Math.round((IY + IH - 1 - foot - words.line('print')) / 2), 'print', 0, 'center');
  }
  function coachPhone(c, open) {
    if (c.firstMinute) return; // the first minute's "Knock 1 of 2" sits in the menu bar (phoneMenu), so the short glass keeps the visitor's head
    const { x, y, w, h } = PF.coach, title = c.firstMinute ? `Knock ${c.step} of ${c.of}` : `Lesson ${c.step} of ${c.of}`;
    words.cover(x, y, w + 3, h + 3);
    fill(x, y, w, h, 0); box(x, y, w, h, 0); label(title, x + 8, y + h / 2, 'title', 1);
    const ax = x + 8 + lw(title, 'title') + 10;
    if (!c.firstMinute) for (let r = 0; r < 4; r++) hline(open ? ax + 3 - r : ax + r, y + 9 + r, open ? 1 + 2 * r : 7 - 2 * r, 1); // up when open, down when folded
    const bar = $('coachbar'), skip = $('coachskip');
    if (bar && !bar.hidden) { place(bar, x, y, w - 62, h); bar.setAttribute('aria-expanded', String(open)); }
    if (skip && !skip.hidden) button(skip, x + w - 58, y, 54, 'Skip', '', false, false);
  }
  // the lesson's text, drawn over the chat's messages (never over the glass): open until the first question
  function coachBox(c, top, bot) {
    const lh = words.line('coach'), x = CX + 6, w = CW - 12, lines = (c.firstMinute && c.step === 1 ? c.phoneLesson.lines : [c.text]).flatMap(l => words.wrap(l, 'coach', w - 20));
    const h = Math.round(bot - top); // all of the message room, so no sliver of a bubble shows under it
    words.cover(x, top, w + 1, h + 1);
    fill(x, top, w, h, 1); box(x, top, w, h, 0);
    // only whole lines inside the box: a line past its bottom edge would sit on the talk field
    const shown = lines.slice(0, Math.max(0, Math.floor((h - 8) / lh)));
    shown.forEach((l, i) => words.say(l, x + 10, Math.round(top + 5 + i * lh), 'coach', 0));
    publish('coach', JSON.stringify({ drawn: shown.length, total: lines.length, top, bottom: top + h }));
  }
  // for the phone checks (test/phone-emulation.test.js): what the canvas drew, as data on it; set only when it changes
  function publish(key, v) { const ds = canvas.dataset; if (ds && ds[key] !== v) { if (v === null) delete ds[key]; else ds[key] = v; } }
  // Sprint 16 (roadmap 26): two plain links under the start dialog, to the overview page and to "What's coming".
  // Sentence text (board role), underlined, each with a hit area (index.html #learn, #coming).
  // Sprint 29 item 2: the third link is Tonight (index.html #tonightlink); its words ("Tonight #7", "Tonight #7, door 3") come from the page.
  // Sprint 29 fix (QA B1): the fourth is the rooms picker (#rooms), "Pick a room"; the phone draws it as a row of its own under the brand row instead.
  const START_LINKS = [['learn', 'How the game works'], ['coming', "What's coming"], ['rooms', 'Pick a room'], ['tonightlink', null]];
  const startLinksH = () => Math.round(words.line('board')) + 8;
  function startLinks(say, x, y, hit = 0, skip = null, skip2 = null) {
    const lh = Math.round(words.line('board'));
    let cx = x;
    for (const [id, label] of START_LINKS) {
      if (id === skip || id === skip2) continue;
      const name = label ?? ($(id)?.textContent ?? '').trim();
      if (!name) continue;
      const w = lw(name, 'board');
      say(name, cx, y, 'board', 0);
      hline(cx, y + lh, w, 0);
      place($(id), cx - 2, y - 2 - hit, w + 4, lh + 5 + 2 * hit);
      cx += w + 10;
    }
  }
  // Launch mode (roadmap 43): the night shift teaser, a card in the start dialog. The words come from index.html #night-teaser
  // (the server leaves it in the page while the night shift is off); the "I'm in" button is its hit area.
  function teaserView(w, compact = false) {
    const t = $('night-teaser');
    if (t?.dataset?.teaser !== 'start') return null;
    const grab = sel => (t.querySelector(sel)?.textContent ?? '').replace(/\s+/g, ' ').trim();
    const btn = t.querySelector('[data-im-in]'), noteEl = t.querySelector('.im-in-note'), note = noteEl && !noteEl.hidden ? noteEl.textContent.trim() : '';
    const blh = words.line('board'), bh = P ? PHONE.touch : 20;
    // compact (a short phone): the one line that says what "I'm in" does (sprint 31 fix pass), not the whole body
    const count = imInCount(), body = `${compact ? START_OFF_WHY : grab('p')}${count ? ` ${count}` : ''}`;
    const bodyLines = words.wrap(body, 'board', w - 16), noteLines = note ? words.wrap(note, 'board', w - 16) : [];
    // compact (a short phone): once "I'm in" has its answer, the answer takes the button's row, so the card does not grow
    const swap = compact && noteLines.length > 0;
    const tight = compact ? 2 : 0; // a short phone has one row to spare: the one body line takes it from the gaps
    const h = Math.round(8 + 16 + bodyLines.length * blh + 6 - tight + (swap ? 0 : bh + (noteLines.length ? 4 : 0)) + noteLines.length * blh + 8 - tight);
    return { tight, title: grab('h3 > .t'), tag: grab('.tag .t'), btn: swap ? null : btn, bodyLines, noteLines, h, blh, bh: swap ? -4 : bh };
  }
  // While the night shift is off the "This shift" window has no pool, ledger or fee split to show: the off lines, in
  // sentence text, then the "I'm in" button (unless the start dialog is up and already holds it).
  const nightOff = () => $('night-teaser')?.dataset?.teaser === 'start';
  const imInCount = () => { const el = $('night-teaser')?.querySelector('.im-in-count'); return el && !el.hidden ? el.textContent.trim() : ''; };
  // the desktop start dialog drops its night card on a short window; the Night shift window then holds "I'm in" (set each frame)
  let teaserDropped = false;
  function offHour(withButton) {
    windowFrame(IX, IY, IW, IH, START_OFF_TITLE);
    const blh = Math.round(words.line('board')), w = IW - 24, foot = IY + IH - 14;
    let iy = IY + 24;
    for (const [s, role] of [[START_OFF_LINE, 'board'], [`${START_OFF_WHY}${imInCount() ? ` ${imInCount()}` : ''}`, 'board'], [START_OFF_ROOM, 'receipt']]) {
      for (const l of words.wrap(s, role, w)) { words.say(l, IX + 12, iy, role, 0); iy += Math.round(words.line(role)) + (role === 'board' ? 1 : 0); }
      iy += 2;
    }
    const btn = $('night-teaser')?.querySelector('[data-im-in]');
    if (withButton && btn) {
      const bw = 92, bh = P ? PHONE.touch : 20, by = P ? iy + 8 : Math.min(iy + 4, foot - bh - 4);
      button(btn, IX + 12, by, bw, btn.textContent.trim(), '', false, btn.disabled, btn.disabled);
      const note = $('night-teaser').querySelector('.im-in-note');
      if (note && !note.hidden && note.textContent.trim()) { // beside the button on the desktop (no room under it), under it on a phone
        const nx = P ? IX + 12 : IX + 12 + bw + 10, ny = P ? by + bh + 4 : by + 1, rl = Math.round(words.line('receipt'));
        words.wrap(note.textContent.trim(), 'receipt', IX + IW - 12 - nx).forEach((l, i) => words.say(l, nx, ny + i * rl, 'receipt', 0));
      }
    }
    if (!P) { hline(IX + 1, foot, IW - 2, 0); words.say(DISCLAIMER, IX + IW / 2, foot + Math.round((IY + IH - 1 - foot - words.line('print')) / 2), 'print', 0, 'center'); }
  }
  function drawTeaser(v, x, y, w, say) {
    fill(x, y, w, v.h, 1); box(x, y, w, v.h, 0);
    const tx = x + 8 + lw(v.title, 'label') + 8, tgw = lw(v.tag, 'tag') + 10;
    label(v.title, x + 8, y + 12, 'label', 0);
    box(tx, y + 6, tgw, 13, 0); label(v.tag, tx + tgw / 2, y + 12.5, 'tag', 0, 'center');
    v.bodyLines.forEach((l, i) => say(l, x + 8, Math.round(y + 24 + i * v.blh), 'board', 0));
    const by = Math.round(y + 24 + v.bodyLines.length * v.blh + 6 - (v.tight ?? 0));
    if (v.btn) button(v.btn, x + 8, by, 92, v.btn.textContent.trim(), '', false, v.btn.disabled, v.btn.disabled);
    v.noteLines.forEach((l, i) => say(l, x + 8, Math.round(by + v.bh + 4 + i * v.blh), 'board', 0));
  }
  // The Big Knock's hall banner (index.html #bkbanner, a 56 px strip fixed under the menu bar) never touches the start dialog: while it shows, the dialog moves
  // down just far enough that its title bar starts below the banner (sprint 29 fix, QA B2). Logical px to add to the dialog's top `nominal`; 0 with no banner.
  function bannerShift(nominal) {
    const b = $('bkbanner');
    if (!b || b.hidden || !b.getBoundingClientRect || !canvas.getBoundingClientRect) return 0;
    const r = canvas.getBoundingClientRect(), k = r.width / canvas.width;
    if (!k) return 0;
    const top0 = r.top + (doc.defaultView?.scrollY ?? 0); // the canvas's top with the page at the top, so scrolling does not move the dialog
    return Math.max(0, Math.ceil((b.getBoundingClientRect().bottom + 4 - top0) / k) - nominal);
  }
  // for the layout checks (studio/sprints/29/bigknock-shoot.js): the start dialog's drawn frame in logical px, as data on the canvas ("" when it is not up)
  function publishStart(rect) {
    const ds = canvas.dataset; if (!ds) return;
    const v = rect ? rect.join(',') : '';
    if (ds.start !== v) ds.start = v;
  }
  // the start dialog's brand row: the 40 x 40 token at one whole pixel per cell, then the name and "Who's there?"
  const BRAND_H = 44;
  // the start dialog opens with the core idea in one line (index.html #startline, game/tutorial.js FIRST_MINUTE_COPY), then the story
  const startStory = width => {
    const story = ($('story')?.textContent ?? '').replace(/\s+/g, ' ').trim(), lead = ($('startline')?.textContent ?? '').replace(/\s+/g, ' ').trim();
    // the core idea is a bold headline of its own (label role), the story paragraph under it
    const head = words.wrap(lead, 'label', width), body = words.wrap(story, 'board', width), hlh = words.line('label'), blh = words.line('board');
    return { head, body, hlh, blh, h: Math.round(head.length * hlh + 4 + body.length * blh) };
  };
  function brandHead(x, y, say) {
    MARK.forEach((row, j) => { for (let i = 0; i < row.length; i++) { S.tint = row[i] === 'r' ? T.red : 0; px(x + i, y + j, row[i] === '.' ? 0 : 1); } });
    S.tint = 0;
    label('Knock', x + 50, y + 12, 'title', 0);
    say("Who's there?", x + 50, Math.round(y + 24), 'board', 0);
  }
  // the Tonight entry in the phone's brand row (phone.js tonightLines): an ink button, paper words, its right edge on `right`.
  // Returns false (and draws nothing) when it does not fit beside the name, so the link row keeps it.
  function tonightBadge(x, y, right) {
    const el = $('tonightlink'), lines = el && !el.hidden ? tonightLines(el.textContent, 5) : null;
    if (!lines) return false;
    const bw = Math.max(lw(lines[0], 'label'), lw(lines[1], 'board')) + 14, bh = BRAND_H - 4, bx = right - bw;
    if (bx < x + 50 + Math.max(lw('Knock', 'title'), lw("Who's there?", 'board')) + 6) return false;
    fill(bx + 2, y + 2, bw, bh, 0); fill(bx, y, bw, bh, 0); box(bx + 2, y + 2, bw - 4, bh - 4, 1);
    label(lines[0], bx + bw / 2, y + 13, 'label', 1, 'center');
    words.say(lines[1], Math.round(bx + bw / 2), Math.round(y + 21), 'board', 1, 'center');
    place(el, bx, y, bw, bh);
    return true;
  }
  // the lines between the name field and the role buttons: the side note (#sidenote), then "N people on the block now" (#onblock)
  // compact (a short phone): the name error and the side note only; the head count and the Big Knock's window line wait for a taller screen
  const startNoteLines = (width, compact = false) => [$('nameerr'), $('sidenote'), $('onblock')].concat($('bknext')).slice(0, compact ? 2 : 4).map(el => (el?.textContent ?? '').replace(/\s+/g, ' ').trim()).filter(Boolean).flatMap(t => words.wrap(t, 'board', width));
  // the start dialog: as wide as the screen, the two role buttons stacked, every control one touch row high
  // Sprint 30 item 1: when the full dialog does not fit above the disclaimer (390 x 664), the compact one keeps the headline, the name,
  // the two role buttons, "I'm in" (title and button; its answer still shows) and the links, and leaves out the story paragraph, the
  // plain-letters line, the head count and the night teaser's paragraph (the story is one tap away at "How the game works");
  // the rooms picker moves from its own row into the links row.
  function phoneStartBox() {
    // Sprint 31 (QA B1): every phone height takes the fullest dialog that still fits above the disclaimer, so "I'm in" and "How the game works" are in view at 390 x 844 and 390 x 700 too
    for (const level of [0, 1]) { const b = phoneStartBoxFor(level); if (b.y0 + b.h + 2 <= PH) return b; }
    return phoneStartBoxFor(2); // the shortest windows also lose the headline
  }
  function phoneStartBoxFor(level) {
    const compact = level > 0;
    const T = PHONE.touch, blh = words.line('board'), plh = words.line('print');
    const x = 4, y0 = PHONE.menuH + 4 + bannerShift(PHONE.menuH + 4), w = SW - 8;
    const story = startStory(w - 24);
    if (compact) { story.h = level > 1 ? 0 : Math.round(story.head.length * story.hlh); story.body = []; if (level > 1) story.head = []; }
    const plain = ($('plain')?.textContent ?? '').replace(/\s+/g, ' ').trim(), plainLines = compact ? [] : words.wrap(plain, 'board', w - 24);
    const printLines = words.wrap('Practice is free. Night shift: hold KNOCK.', 'print', w - 24);
    // "Your doorkeeper doors are done for this shift: go knock.": its own lines between the name field and the buttons
    const noteLines = startNoteLines(w - 24, compact);
    const nS = noteLines.length ? Math.round(noteLines.length * blh) + 8 : 0;
    const ROW = $('rooms') && !compact ? T + 8 : 0; // the rooms picker's own touch row, under the brand row (QA B1; compact: in the links row)
    const top = BRAND_H + ROW + 22 + story.h + 6, ny = y0 + top + 16 + T + 6, by = y0 + top + 16 + T + 12 + nS;
    const tv = teaserView(w - 24, compact), midH = tv ? tv.h + 4 : printLines.length * plh;
    const py = by + 2 * T + 10 + 8, h = Math.round(py - y0 + plainLines.length * blh + 4 + midH + 8 + startLinksH());
    return { x, y0, w, h, top, by, py, ny, ROW, story, noteLines, plainLines, printLines, tv, midH, compact };
  }
  function phoneStart(start, now, say, box) {
    const T = PHONE.touch, blh = words.line('board'), plh = words.line('print');
    const { x, y0, w, h, top, by, py, ny, ROW, story, noteLines, plainLines, printLines, tv, midH } = box;
    words.cover(x, y0, w + 3, h + 3);
    windowFrame(x, y0, w, h, 'Pick a side');
    brandHead(x + 12, y0 + 21, say);
    const badge = tonightBadge(x + 12, y0 + 21, x + w - 12);
    const rooms = $('rooms'); // Sprint 29 fix (QA B1): a touch row of its own, outlined, under the brand row
    if (rooms && ROW) button(rooms, x + 12, y0 + 21 + BRAND_H + 4, w - 24, 'Pick a room', '', false, false);
    story.head.forEach((l, i) => say(l, x + 12, Math.round(y0 + 21 + BRAND_H + ROW + i * story.hlh), 'label', 0));
    story.body.forEach((l, i) => say(l, x + 12, Math.round(y0 + 21 + BRAND_H + ROW + story.head.length * story.hlh + 4 + i * story.blh), 'board', 0));
    label('Your name', x + 12, y0 + top + 7, 'label', 0);
    field($('name'), x + 12, y0 + top + 16, w - 24, T, 'your name', now, 'chat');
    const [dk, vs] = start.querySelectorAll('button');
    noteLines.forEach((l, i) => say(l, x + 12, Math.round(ny + i * blh), 'board', 0));
    // a used-up side is drawn dotted and loses the default ring to the open one
    if (dk) button(dk, x + 12, by, w - 24, dk.textContent.trim(), '', !dk.disabled, dk.disabled);
    if (vs) button(vs, x + 12, by + T + 10, w - 24, vs.textContent.trim(), '', dk?.disabled && !vs.disabled, vs.disabled);
    plainLines.forEach((l, i) => say(l, x + 12, Math.round(py + i * blh), 'board', 0));
    const qy = Math.round(py + plainLines.length * blh + 4);
    if (tv) drawTeaser(tv, x + 12, qy, w - 24, say);
    else printLines.forEach((l, i) => say(l, x + w / 2, Math.round(qy + i * plh), 'print', 0, 'center'));
    // a touch row high on a phone (44 px on screen)
    startLinks(say, x + 12, Math.round(qy + midH + 6), Math.max(0, Math.ceil((T - Math.round(blh) - 5) / 2)), badge ? 'tonightlink' : null, box.compact ? 'coming' : 'rooms');
  }
  // the timer: seconds in the title bar's right end, and a bar under the title that runs down; outside a round,
  // the wait line sits just above the talk field
  function phoneTimer(inRound, secsLeft, nervous, SP, outbreak, fr, phase, waitBot, total, redDigits) {
    const y = PF.timer.y, x0 = CX + 1, w = CW - 2;
    hline(x0, y + PF.timer.h, w, 0);
    if (!inRound) {
      const msg = phase === 'queued' ? 'waiting for a knock' : phase === 'start' ? 'no knock yet' : '';
      if (msg) words.say(msg, CX + 12, Math.round(waitBot - words.line('chat') - 2), 'chat', 0);
      return;
    }
    const n = String(secsLeft), nw = tw(n, 'mid');
    // the last 10 s on a plain or gold door: red digits on an ink tab (red moment 1)
    fill(CX + CW - nw - 18, CY + 1, nw + 12, 15, redDigits ? 0 : 1);
    if (redDigits) { S.tint = T.red; text(n, CX + CW - 12, CY + 2, 'mid', 1, 'right'); S.tint = 0; } else text(n, CX + CW - 12, CY + 2, 'mid', 0, 'right');
    const fw = Math.round(w * Math.min(1, secsLeft / total));
    // time left is ink; time used is an empty trough, edged in ink, so a sliver of used time reads as the bar running down
    fill(x0, y, fw, PF.timer.h, 0);
    fill(x0 + fw, y, w - fw, PF.timer.h, 1);
    hline(x0 + fw, y, w - fw, 0);
    fill(x0 + w - 1, y, 1, PF.timer.h, 0);
    if (nervous && fr % 4 < 2) { // the nervous flash: the bar, and the tab too unless its digits are the red ones (they stay red on ink every frame)
      xfill(x0, y, w, PF.timer.h); if (!redDigits) xfill(CX + CW - nw - 18, CY + 1, nw + 12, 15);
      const tab = CX + CW - nw - 18;
      if (SP || outbreak) for (let j = CY + 1; j < y + PF.timer.h; j++) for (let i = x0; i < x0 + w; i++) if (B.bits[j * SW + i] && !(redDigits && j < CY + 16 && i >= tab && i < tab + nw + 12)) B.tint[j * SW + i] = SP || T.green;
    }
  }
  // the visitor is typing: on a phone the three dots sit in the title bar, beside the name, so they never take
  // the room of the question you asked (the desktop keeps its typing bubble)
  function phoneTyping(title, fr, secsX) {
    const tW = lw(titleOf(title, CW), 'title'), w = 22;
    let x = Math.round(CX + CW / 2 + tW / 2 + 6);
    if (x + w > secsX - 2) x = Math.round(CX + CW / 2 - tW / 2 - 6) - w; // no room right of the name: left of it
    if (x < CX + 23) return;
    fill(x, CY + 1, w, 15, 1);
    const a = fr % 9 / 3 | 0;
    for (let k = 0; k < 3; k++) { const dx = x + 4 + k * 6; if (k === a) fill(dx, CY + 6, 3, 4, 0); else box(dx, CY + 7, 3, 3, 0); }
  }
  // the envelope's window on a phone (phone.js mailFit, client/mail.css): a class and four whole-px variables on <html>, set only when
  // they change. null (the desktop layout): the class comes off and mail.css places the window as before.
  // desk: on the desktop layout, the window's top from phone.js mailFitDesk (clear of the sum on the door), or null.
  let mailKey = '';
  function fitMail(fit, desk = null) {
    const root = doc.documentElement; if (!root?.style) return;
    const k = fit ? `${fit.short ? 1 : 0},${fit.top},${fit.sceneH},${fit.a},${fit.b}` : `d${desk ?? ''}`;
    if (k === mailKey) return;
    mailKey = k;
    root.classList?.toggle('mail-short', !!fit?.short);
    root.classList?.toggle('mail-desk', desk !== null);
    if (desk !== null) root.style.setProperty?.('--mail-dtop', desk + 'px'); else root.style.removeProperty?.('--mail-dtop');
    for (const [n, v] of [['top', fit?.top], ['sh', fit?.sceneH], ['a', fit?.a], ['b', fit?.b]]) {
      if (fit) root.style.setProperty?.(`--mail-${n}`, v + 'px'); else root.style.removeProperty?.(`--mail-${n}`);
    }
  }
  // the desktop: the sum's slab in CSS px (the canvas is drawn at a whole scale from the page's top left)
  function deskMailTop(st) {
    if (!st.sum || !sumBox || !canvas.getBoundingClientRect) return null;
    const r = canvas.getBoundingClientRect(), k = r.width / canvas.width, vh = doc.defaultView?.innerHeight;
    if (!k || !vh) return null;
    return mailFitDesk({ t: r.top + sumBox[1] * k, b: r.top + (sumBox[1] + sumBox[3]) * k }, vh, r.top + 18 * k);
  }
  // for the layout checks (studio/sprints/29/art-shoot-item2.js): the sum's slab and the chat window's title bar (the visitor's name), in
  // logical px, as data on the canvas; set only when they change
  function publishBoxes(st) {
    const ds = canvas.dataset; if (!ds) return;
    const sum = st.sum && sumBox ? sumBox.join(',') : '', chat = `${CX},${CY},${CW},17`;
    if (ds.sum !== sum) ds.sum = sum;
    if (ds.chat !== chat) ds.chat = chat;
  }
  // the jack-o'-lantern (jack.js, halloween.css) sits on top of tonight's rule, between its two strips of tape
  // While the Knocker shows, its fingers hook over that same edge: the jack moves along it, clear of both hands (sprint 33 item 1 fix pass)
  let jackKnocker = false; // set each phone frame: the Knocker shows
  function phoneJack(startOn, noteY = PF.note.y) {
    const jack = $('jack'); if (!jack) return;
    const N = PF.note, hands = jackKnocker ? (knockerSheet()?.handSpans ?? []).map(([a, b]) => [GX + a, GX + b]) : [];
    const left = `${clearOf(N.x + N.w - 18 - 26, 22, hands, N.x, N.x + N.w)}px`, top = `${(N.h ? noteY : CY) - 17}px`;
    if (jack.style.left !== left) jack.style.left = left;
    if (jack.style.top !== top) jack.style.top = top;
    const vis = startOn ? 'hidden' : ''; // the start dialog covers the door on a phone
    if (jack.style.visibility !== vis) jack.style.visibility = vis;
  }

  // the verdict alert's box over the chat window. Phone: as wide as the chat window, the head wrapped, the note
  // under the icon, as tall as it needs, sitting on the bottom of the chat window
  function alertBox(v, card = null) {
    if (card) return cardBox(v, card);
    const fits = layoutAlert(v, v.note);
    if (!P || !v.slip) return fits;
    // phone: the slipped bubble (and its ring) must fit above the alert; if the full note leaves no room, the note
    // drops tonight's rule (it is on the notice already) and keeps the caption
    const need = Math.round(words.wrap(v.slip.text, 'chat', CW - 46).length * words.line('chat')) + PHONE.bubble.pad + 4; // + the ring above
    return fits.AY - 5 - PF.msgs.y >= need ? fits : layoutAlert(v, v.slip.short);
  }
  // The reveal card with the verdict merged in (sprint 27 item 3 fix pass, reveal-card.js): one window in the alert's
  // place, as wide as the chat window and sitting on its bottom (desktop and phone), so the glass and its reveal stay
  // in view. With a slip, the slipped line must still show above it: the why line then drops tonight's rule, and the
  // quote and the why line give up lines until it does.
  const pointsText = v => `${v.points > 0 ? '+' : v.points < 0 ? '-' : ''}${Math.abs(v.points)}`;
  // "Play this again?" (sprint 30 item 3 fix pass): the fifth free-room door adds a row over the buttons for the question, its own line. On a phone
  // the Share button takes that row's right end (the buttons row holds the two answers); on the desktop Share keeps the buttons row's left end.
  // the envelope tray's chips (Lamp, Keyring, Session) wrap to a second row when the lamp is lit: the event plate goes under their measured bottom (plus 4 px), in canvas rows
  const trayRows = () => {
    const chips = [...($('mail')?.querySelectorAll?.('.mail-tray>span:not(:empty)') ?? [])];
    if (!chips.length) return 0;
    const r = canvas.getBoundingClientRect?.();
    if (!r || !r.width) return true; // not measurable (a test's stub): the one-row figure
    const k = r.width / canvas.width, bottom = Math.max(...chips.map(c => c.getBoundingClientRect().bottom)) + 2; // + the chip's 2 px shadow
    return Math.max(TRAY_BOTTOM, Math.ceil((bottom - r.top) / k) + 2);
  };
  const shareUp = () => { const sh = $('share'); return !!sh && !sh.hidden; };
  const againH = v => !v.again ? 0 : Math.round(P && shareUp() ? PHONE.touch : words.line('label')) + 8;
  function againLayout(AX, AW, by2) {
    const T = P ? PHONE.touch : 20, yes = $('next').textContent.trim(), no = $('ano').textContent.trim(), pad = P ? 12 : 20, gap = P ? 10 : 12, edge = P ? 10 : 12;
    const wy = Math.ceil(lw(yes, 'label')) + pad, wn = Math.ceil(lw(no, 'label')) + pad, bx = AX + AW - edge - wn, nx = bx - gap - wy;
    const sw = P ? Math.ceil(lw('Share', 'label')) + pad : 64;
    return { nx, nw: wy, bx, bw: wn, qy: by2 - 6 - (P && shareUp() ? T : Math.round(words.line('label'))) / 2, sx: P ? AX + AW - edge - sw : AX + 12, sy: P ? by2 - 6 - T : by2, sw };
  }
  function cardBox(v, card) {
    const AW = CW - 8, AX = CX + 4, bottom = CY + CH - 4, top = P ? PF.msgs.y : CY + 20;
    const extra = (note, noQuote, compact) => ({ points: pointsText(v), note: v.miniLine ? `${v.miniLine} ${note}` : note, noteMax: v.fm ? 8 : P ? 5 : 4, foot: (P ? PHONE.touch + 14 : 34) + againH(v), noQuote: noQuote || (P && v.again), compact }); // the prompt costs a row: a phone's card gives up the quoted line, never the why
    const make = (note, maxH, noQuote = false, compact = false) => { const lay = layoutCard(card, words, AW, maxH, extra(note, noQuote, compact)); return { card: true, lay, AW, AH: lay.h, AX, AY: bottom - lay.h }; };
    // the card's quote is the slipped line: the note leaves out the caption that would quote it again (cardNote)
    const note = v.cardNote ?? v.note;
    if (!v.slip) {
      const a = make(note, bottom - ((P ? PF.chat.y : CY) + 20)), whole = b => !b.lay.verdict.cut && !b.lay.quoteCut; // sprint 31: a quote squeezed to "The..." is worse than none
      if (whole(a)) return a;
      // a why line that would be cut (the first minute's is long) may take the chat window's title row too: the card names them
      const b = make(note, bottom - ((P ? PF.chat.y : CY) + 2));
      if (whole(b)) return b;
      // sprint 31: still cut: the why line is the lesson, so the quoted line goes (the why line names what was said), then the card's title bar
      const c = make(note, bottom - ((P ? PF.chat.y : CY) + 2), true);
      return c.lay.verdict.cut ? make(note, bottom - ((P ? PF.chat.y : CY) + 2), true, true) : c;
    }
    const a = make(note, bottom - ((P ? PF.chat.y : CY) + 20));
    const need = Math.round(words.wrap(v.slip.text, 'chat', P ? CW - 46 : 232).length * words.line('chat')) + PHONE.bubble.pad + 4;
    if (a.AY - 5 - top >= need && !a.lay.verdict.cut) return a;
    // Sprint 31: the slipped line must have its room (it was left out when the card could not shrink enough: an empty band, a cut caption). Each try is
    // kept only if it is whole and fits; the order: the note as it is, tonight's rule gone, then the card's own copy of the quote, then the caption's reason
    // alone, then without the "You kept the door shut." line; if none fits, the same tries with the card's title bar left out (compact).
    const room = bottom - (top + need + 5), short = v.slip.cardShort ?? v.slip.short, reason = v.slip.cardReason ?? short;
    const ok = b => !b.lay.verdict.cut && !b.lay.quoteCut && b.AH <= room;
    const tries = c => [() => make(note, room, false, c), () => make(short, room, false, c), () => make(note, room, true, c), () => make(reason, room, true, c), () => make(reason, room, 'bare', c), ...(v.slip.cardTiny ? [() => make(v.slip.cardTiny, room, 'bare', c)] : [])];
    for (const c of [false, true]) for (const t of tries(c)) { const b = t(); if (ok(b)) return b; }
    return make(v.slip.cardTiny ?? reason, room, 'bare', true);
  }
  function layoutAlert(v, note) {
    // the head is a title (bold sentence text), wrapped to two lines at most on the desktop
    // with a slip (roadmap 30) the desktop alert is as wide as the chat window and sits on its bottom, as on a phone,
    // so the slipped line shows above it and the talk field and the call buttons stay hidden under it
    const DW = v.slip ? CW - 8 : 292;
    const headLines = words.wrap(v.head, 'title', P ? CW - 8 - 66 : DW - 66).slice(0, P ? 4 : 2), vlh = words.line('verdict');
    const verdictLines = words.wrap(note || '', 'verdict', P ? CW - 8 - 28 : DW - 66).slice(0, v.fm ? 6 : P ? 4 : 3); // a first-minute why line is shown whole
    const headH = (headLines.length - 1) * 14, noteY = (P ? 56 : 50) + headH;
    const AW = P ? CW - 8 : DW, AH = P ? Math.round(noteY + verdictLines.length * vlh + 8 + PHONE.touch + 10) : 118 + headH + (v.fm ? Math.round(Math.max(0, verdictLines.length - 3) * vlh) : 0);
    const AX = P ? CX + 4 : CX + (CW - AW) / 2 | 0, AY = P || v.slip ? Math.max(CY + 20, CY + CH - 4 - AH) : CY + 34;
    return { headLines, verdictLines, headH, noteY, AW, AH, AX, AY };
  }

  // the floor (desktop): from the wall's base (row 336) to the bottom of the window. It is lit from the door: a pool
  // of light brightest just in front of the step, fading out sideways and towards the viewer, near ink under the
  // windows on the right. The step stands on it. Boards run towards the door and spread apart towards the viewer,
  // as solid ink seams with a paper edge beside them where the light is strong enough to show them.
  function drawFloor(lamp) {
    const top = 336, cx = 160, vy = 236, ry = Math.max(110, (SH - top) * 1.4);
    const light = (x, y) => 0.02 + 0.5 * Math.max(0, 1 - Math.hypot((x - cx) / 230, (y - 372) / ry)) ** 1.3 * lamp;
    const stepAt = y => { const q = (y - 337) / 23; return y >= 337 && y < H ? [Math.round(78 - q * 40), Math.round(242 + q * 40)] : null; };
    const onStep = (x, y) => { const s = stepAt(y); return !!s && x >= s[0] - 1 && x <= s[1]; };
    fill(0, top, SW, SH - top, (x, y) => dz(x, y, light(x, y)));
    // boards: seams on lines through the vanishing point (cx, vy), 18 px apart at the wall's base
    const S0 = 18, K = Math.ceil(Math.max(cx, SW - cx) * (SH - vy) / (top - vy) / S0) + 1;
    for (let y = top + 1; y < SH; y++) {
      const sc = (y - vy) / (top - vy);
      for (let k = -K; k <= K; k++) {
        const x = Math.round(cx + (k + 0.5) * S0 * sc);
        if (x < 0 || x >= SW || onStep(x, y)) continue;
        const v = light(x, y);
        if (v < 0.07) continue;
        px(x, y, 0);
        if (x + 1 < SW && !onStep(x + 1, y)) px(x + 1, y, 1);
      }
    }
    // board ends: short ink joints, staggered, further apart towards the viewer
    for (let j = 0, y = top + 7, gap = 9; y < SH; j++, y += gap, gap += 3) {
      const sc = (y - vy) / (top - vy);
      for (let k = -K; k < K; k++) {
        if (hash(k + 99, j) > 0.22) continue;
        const a = Math.round(cx + (k + 0.5) * S0 * sc) + 1, b = Math.round(cx + (k + 1.5) * S0 * sc);
        for (let x = Math.max(0, a); x < Math.min(SW, b); x++) if (!onStep(x, y) && light(x, y) >= 0.07) px(x, y, 0);
      }
    }
    // the step: a lit slab with treads, an ink edge down each side, and its shadow on the floor in front of it
    for (let y = 337; y < H; y++) {
      const [x0, x1] = stepAt(y), q = (y - 337) / 23;
      for (let x = x0; x < x1; x++) { const e = Math.min(x - x0, x1 - x) / 30; px(x, y, dz(x, y, (0.3 + Math.min(0.55, e)) * (1 - q * 0.35) * lamp)); }
      px(x0 - 1, y, 0); px(x1, y, 0);
      if ((y - 338) % 5 === 0) for (let x = x0; x < x1; x++) if (get(x, y)) px(x, y, 0);
    }
    if (SH > H) {
      const [x0, x1] = [38, 282];
      hline(x0 - 1, H, x1 - x0 + 2, 0);
      for (let x = x0; x < x1; x++) if ((x + H + 1) & 1) px(x, H + 1, 0);
    }
  }

  /* ---------- the sum on the door (sum.js, roadmap 27) ----------
     Chalk: paper pixels with a few dropped out, in Jersey 10 (the big numeral face), on an ink slab with a dotted chalk edge.
     One step per beat; the newest is drawn rougher for two frames, as if just scratched on. Colour only on the special's step. */
  const CH_LH = 16, CH_PAD = 6;
  let sumBox = null; // the sum's slab as last drawn, logical px (published on the canvas for the layout checks)
  // where each step goes: stacked and right-aligned (a column sum) on the door; flowed in rows on a narrow phone
  function chalkLayout(steps, w, stacked) {
    if (stacked) return { rows: steps.length, at: steps.map((s, i) => ({ s, row: i, x: w - CH_PAD - tw(s.text, 'big') })) };
    let x = CH_PAD, row = 0;
    const at = steps.map(s => {
      const sw = tw(s.text, 'big');
      if (x > CH_PAD && x + sw > w - CH_PAD) { x = CH_PAD; row++; }
      const p = { s, row, x }; x += sw + 9; return p;
    });
    return { rows: row + 1, at };
  }
  function chalkSum(sum, now, fr, rect, stacked) {
    const age = sum.hold ?? now - sum.at; // a demo may hold the sum at one moment (&sumt=)
    const shown = stepsShown(sum.steps.length, age, sum.reduced), lay = chalkLayout(sum.steps, rect.w, stacked);
    const lh = CH_LH, h = lay.rows * lh + 8; // the slab is as tall as the whole sum, so it does not grow as steps are added
    const y0 = stacked ? Math.min(rect.y, rect.bottom - h) : rect.bottom - h;
    sumBox = [rect.x, y0, rect.w, h];
    words.cover(rect.x, y0, rect.w + 1, h + 1);
    fill(rect.x, y0, rect.w, h, 0);
    for (let i = 0; i < rect.w; i += 2) { px(rect.x + i, y0, 1); px(rect.x + i, y0 + h - 1, 1); }
    for (let j = 0; j < h; j += 2) { px(rect.x, y0 + j, 1); px(rect.x + rect.w - 1, y0 + j, 1); }
    lay.at.slice(0, shown).forEach(({ s, row, x }, i) => {
      const jx = Math.round((hash(i, 3) - 0.5) * 3), y = y0 + 4 + row * lh;
      const fresh = !sum.reduced && age - (SUM_FIRST_MS + i * SUM_STEP_MS) < 140; // just scratched on: rougher for two frames
      const keep = fresh ? 0.55 : P ? 0.97 : 0.9; // phone digits are few pixels wide: a dropped pixel breaks them
      if (s.kind === 'total') { for (let k = 0; k < rect.w - 2 * CH_PAD - 10; k += 2) if (hash(k, 17) < 0.85) px(rect.x + CH_PAD + 5 + k, y - 2, 1); }
      S.tint = s.special ? T[s.special] : 0;
      text(s.text, rect.x + x + jx, y, 'big', 1, 'left', (X, Y) => hash(X * 7 + 3, Y * 13 + i) < keep);
      S.tint = 0;
    });
    return y0; // the slab's top (the phone's jack-o'-lantern sits on it while the sum shows)
  }
  /* ---------- chalk tallies beside the door (tally.js, roadmap 28) ----------
     The multiplier the next door pays, big, over the tallies in groups of five. A wipe smears them: the chalk is dragged
     sideways row by row and drops out, and a few smudged dashes are left for a moment. Plain paper on ink; no colour. */
  const TL_PHONE = { h: 14, gap: 5, stroke: 2, groupW: 17, groupGap: 0, rowH: 19 }, TL_DESK = { ...TL_PHONE, h: 18, rowH: 23 }, TALLY_EYE = 84;
  function tallyPlate(tal, now, x, y) {
    const TL = P ? TL_PHONE : TL_DESK;
    const smear = tal.smear, age = smear ? smear.age : 0, shownN = smear ? smear.n : tal.n;
    if (!shownN || (smear && age > SMEAR_MS + 2200)) return;
    const p = smear ? clamp(age / SMEAR_MS) : 0, groups = tallyGroups(shownN), perRow = 1;
    const mult = streakText(smear ? shownN : tal.n), mw = tw(mult, 'big');
    const NEXT = 'next', nw = lw(NEXT, 'tag'), nh = words.line('tag'); // the multiplier is the next door's, so the plate says so
    const gw = perRow * TL.groupW + (perRow - 1) * TL.groupGap, w = Math.max(mw, gw, nw) + 4, rows = Math.ceil(groups.length / perRow);
    const h = nh + 5 + 16 + 4 + rows * TL.rowH + 3, px0 = P ? x : Math.max(1, Math.min(x, 33 - w));
    fill(px0, y, w, h, 0);
    for (let i = 0; i < w; i += 2) { px(px0 + i, y, 1); px(px0 + i, y + h - 1, 1); }
    for (let j = 0; j < h; j += 2) { px(px0, y + j, 1); px(px0 + w - 1, y + j, 1); }
    const drag = yy => Math.round(p * 7 * (0.4 + hash(yy, 41))); // each row is dragged its own way
    const chalk = (X, Y, k) => hash(X * 7 + 3, Y * 13 + k) < 0.97 - p * 0.85;
    const dot = (X, Y, k) => { if (chalk(X, Y, k)) px(X + (smear ? drag(Y) : 0), Y, 1); };
    if (!smear) label(NEXT, px0 + Math.round(w / 2), y + 3 + nh / 2, 'tag', 1, 'center');
    for (const bold of P ? [0] : [0, 1]) text(mult, px0 + Math.round((w - mw) / 2) + bold, y + 3 + nh, 'big', 1, 'left', (X, Y) => hash(X * 7 + 3, Y * 13 + 5) < 0.96 - p * 0.8); // desktop: struck twice, a heavier numeral
    const gx0 = px0 + Math.round((w - gw) / 2), gy0 = y + nh + 5 + 16 + 4;
    groups.forEach(({ strokes, struck }, gi) => {
      const gx = gx0 + (gi % perRow) * (TL.groupW + TL.groupGap), gy = gy0 + Math.floor(gi / perRow) * TL.rowH;
      for (let s = 0; s < strokes; s++) for (let j = 0; j < TL.h; j++) for (let t = 0; t < TL.stroke; t++) dot(gx + s * TL.gap + t, gy + j, gi * 9 + s);
      if (struck) for (let i = 0; i < TL.groupW + 1; i++) { const sy = gy + TL.h - 1 - Math.round(i * (TL.h - 1) / TL.groupW); dot(gx - 1 + i, sy, 70 + gi); dot(gx - 1 + i, sy + 1, 80 + gi); }
    });
    if (smear && p >= 1) for (let k = 0; k < 14; k++) { // what is left: a few dashes dragged across the plate
      const yy = gy0 + 1 + Math.floor(hash(k, 61) * (rows * TL.rowH - 3)), xx = gx0 + Math.floor(hash(k, 67) * (gw - 7));
      for (let i = 0; i < 3 + Math.floor(hash(k, 71) * 5); i++) if (hash(xx + i, yy) < 0.7) px(xx + i, yy, 1);
    }
  }
  // the shift total in the corner: "Shift" and a Jersey 10 number on a small ink plate
  function shiftCorner(total, x, y, right = false) {
    const num = String(total), nw = tw(num, 'big'), lbl = 'Shift', lbw = lw(lbl, 'tag'), w = 5 + lbw + 5 + nw + 8, h = 19;
    const px0 = right ? x - w : x;
    fill(px0, y, w, h, 0);
    for (let i = 0; i < w; i += 2) { px(px0 + i, y, 1); px(px0 + i, y + h - 1, 1); }
    for (let j = 0; j < h; j += 2) { px(px0, y + j, 1); px(px0 + w - 1, y + j, 1); }
    label(lbl, px0 + 5, y + h / 2, 'tag', 1);
    text(num, px0 + 10 + lbw, y + 3, 'big', 1);
  }

  // The glass drawn whole on a raster of its own (180 x 166 with the Bayer phase it has on screen), for the phone's punch-in: the glass on screen
  // is cropped by the menu bar, the rule and the chat, and a punch-in shows rows the screen does not.
  let punchB = null;
  function punchGlass(gs) {
    punchB ??= createBits(GW + 8, GH + 8);
    punchB.bits.fill(0); punchB.tint.fill(0);
    const ox = GX & 7, oy = GY & 7;
    drawGlass(punchB, ox, oy, gs);
    return { B: punchB, ox, oy };
  }

  function draw(st) {
    framesDrawn.length = 0;
    const now = st.now;
    const fr = Math.floor(now / 1000 * FPS), t = fr / FPS;
    const inRound = st.phase === 'round' && st.round;
    const showVisitor = st.role === 'doorkeeper' && st.visitor && (st.phase === 'round' || st.phase === 'reveal' || st.phase === 'reacting');
    const roundLen = roundSeconds(st.round);
    const roundT = st.round ? Math.floor((roundLen * 1000 - (st.round.endsAt - st.wall)) / 1000 * FPS) / FPS : 99;
    const revealT = st.revealAt != null ? Math.floor((now - st.revealAt) / 1000 * FPS) / FPS : -1;
    // a gold or red door keeps its light through the silence and the landing too (call-lands.js): the bigger stakes must show
    const special = st.role === 'doorkeeper' && (inRound || st.phase === 'reveal' || st.phase === 'reacting') ? st.special : null;
    const SP = special === 'gold' ? T.gold : special === 'red' ? T.red : 0;
    // a bull or bear knock: behind the glass from the knock until the verdict is put away (the reaction too)
    const beast = st.role === 'doorkeeper' && showVisitor && isBeast(st.special) ? st.special : null;
    const litLook = beast ? litOf(beast, st.look) : null; // a bull knock in a lit knock's look (neighbours-lit.js): only the look differs
    const outbreak = !!st.outbreak;
    const secsLeft = inRound ? Math.max(0, Math.ceil((st.round.endsAt - st.wall) / 1000)) : 0;
    const reveal = st.phase === 'reveal' || st.phase === 'reacting' ? st.reveal : null;
    const rkind = reveal?.kind ?? null;
    // every call lands (call-lands.js, sprint 27 item 3): the stamp and silence after a call, then the landing's lunge, warm glow and flash
    const call = st.call ?? null;
    S.tint = 0; S.ox = 0; S.clip = null;
    words.begin();
    const say = (s, x, y, role, v = 0, align = 'left', clip = S.clip, px = null) => words.say(s, x + S.ox, y, role, v, align, clip, px);
    const LH = role => words.line(role);
    const rule = ($('bulletin')?.textContent ?? '').trim(), hasRule = rule && rule !== '…';
    // phone: tonight's rule sets how far down the chat window starts, so the frame is laid out each frame
    // the first minute (sprint 27): tonight's rule is one line, as large as fits (16 px on screen at the least), and never folded
    const fm = !!st.coach?.firstMinute;
    chrome = firstMinuteChrome(!!st.fm);
    const ruleRoom = (P ? SW - 8 : 228 - 16) - 18, rulePx = fm && hasRule ? words.fit(rule, 'rule', ruleRoom, RULE_MIN_PX, RULE_MAX_PX) : null;
    const ruleLH = rulePx ? words.line('rule', rulePx) : LH('rule');
    let noteLines = null;
    if (P) {
      noteLines = hasRule ? (fm ? [rule] : words.wrap(rule, 'rule', SW - 8 - 18).slice(0, 4)) : [];
      PF = phoneFrame(SW, PH, noteLines.length ? Math.round(27 + noteLines.length * ruleLH) : 0, fm, !nightOff());
      ({ x: GX, y: GY } = PF.glass); ({ x: DX, y: DY, w: DW, h: DH } = PF.door);
      ({ x: CX, y: CY, w: CW, h: CH } = PF.chat); if (PF.hour) ({ x: IX, y: IY, w: IW, h: IH } = PF.hour);
    }

    // knocks, lamp, visitor motion
    let knockAmt = 0, flash = false;
    if (st.round && st.phase !== 'start') for (const k of KNOCKS) { const d = roundT - k; if (d >= 0 && d < 0.2) { knockAmt = Math.max(knockAmt, 1 - d / 0.2); if (d < 0.09) flash = true; } }
    const shake = (knockAmt > 0 ? [2, -2, 1, -1, 1][fr % 5] : 0) + (call?.jolt ?? 0); // a knock rattles the door; the ka-thunk jolts it once
    let lamp = 1 + Math.sin(t * 2.1) * 0.05 + Math.sin(t * 5.3) * 0.02;
    if (st.flicker < 1) lamp *= 0.75;
    const nervous = inRound && secsLeft <= 10 && secsLeft > 0;
    // red moment 1: the last 10 s of a plain or gold door (not a red, bull, bear or lit knock): the creep, red digits, the visitor leaning in a step every 3 s
    const last10 = !!(showVisitor && inRound && lastTenOn(special, secsLeft));
    const lean = last10 ? leanOf(secsLeft, !!st.reduced) : 1;
    if (nervous) { const r = hash(fr, 7); lamp = r < 0.25 ? 0.25 : r < 0.4 ? 0.6 : 1; }
    if (rkind && revealT >= 0) lamp = revealLamp(rkind, revealT, fr, t);
    if (call?.warm) lamp *= 1 + call.warm; // a right call: the porch lamp glows up for a moment
    if ((call?.lunge ?? 1) > 1) lamp = Math.max(lamp, 1.12); // the lunge happens in full light: the porch lamp holds, so the shape reads
    const step = showVisitor ? (st.phase === 'round' ? ease((roundT - 1.5) / 1.0) : 1) : 0;
    // the eyes follow the room (eyes.js): the question you type, the hand over Open, the visitor typing, and blinks from the visitor's seed
    const hover = canHover(doc), hand = el => handOn(el, hover); // a tap on a phone leaves no hover or focus behind that counts
    const eye = showVisitor ? eyeTrack({
      seed: seedOf(JSON.stringify(st.visitor)), frame: inRound ? Math.floor(roundT * FPS) : 0, reduced: !!st.reduced,
      you: !!$('ask')?.value, open: hand($('open')), think: [...($('chat')?.children ?? [])].some(e => String(e.className).includes('typing')),
    }) : null;
    const slip = !!reveal?.view?.slip;

    /* ================= the room ================= */
    if (!P) {
      fill(0, 0, SW, H, (x, y) => { const gl = Math.max(0, 1 - Math.hypot(x - 160, (y - 150) * 0.9) / 300) * Math.min(1.15, lamp); return dz(x, y, 0.02 + gl * gl * 0.16); });
      hline(0, 232, CX - 8, 1); hline(0, 234, CX - 8, 1);
      // the wall's base across the whole width: an ink ground with the skirting line on row 335, then drawFloor
      // paints the lit floor over it from row 336 to the bottom of the window (no ink strip is left showing)
      fill(0, 335, SW, 25, 0); hline(0, 335, CX - 8, 1);
      drawFloor(Math.min(1.15, lamp));
    } else {
      // phone: a close-up of the door fills the view between the menu bar and the chat window
      fill(0, 0, SW, SH, 0);
      S.clip = [0, PF.menuH, SW, CY - PF.menuH];
    }

    /* ---- door ---- */
    S.ox = shake;
    fill(DX - 12, DY - 12, DW + 24, DH + 12, 0);
    S.tint = SP; S.art = ART.TRIM;
    box(DX - 12, DY - 12, DW + 24, DH + 13, 1); box(DX - 9, DY - 9, DW + 18, DH + 10, 1);
    S.art = 0;
    // the leaf's pixels carry their place on the night door's Aseprite leaf (night.js); a one-bit frame never reads it
    fill(DX, DY, DW, DH, (x, y) => {
      S.art = leafArt(x - DX, y - DY);
      const grain = ((x * 7 + Math.floor(Math.sin(y * 0.05 + x) * 3)) % 11 === 0) ? 0.18 : 0;
      const lift = Math.max(0, 1 - Math.hypot(x - 160, y - 140) / 170) * 0.22 * lamp;
      return dz(x, y, 0.04 + lift + grain);
    });
    S.art = ART.TRIM; box(DX, DY, DW, DH, 1); S.art = 0;
    if (SP) {
      // marching outline: the door calls for attention
      const per = [];
      for (let i = 0; i < DW + 30; i += 2) per.push([DX - 15 + i, DY - 15], [DX - 15 + i, DY + DH + 2]);
      for (let j = 0; j < DH + 17; j += 2) per.push([DX - 15, DY - 15 + j], [DX + DW + 14, DY - 15 + j]);
      per.forEach(([x, y], k) => { if ((k + fr) % 6 < 3) { px(x, y, 1); px(x + 1, y, 1); } });
      if (SP === T.gold) for (let k = 0; k < 14; k++) {
        if (hash(k, fr >> 1) < 0.5) {
          const x = DX + 4 + Math.floor(hash(k, 9 + (fr >> 1)) * (DW - 8)), y = DY + 4 + Math.floor(hash(k, 5 + (fr >> 1)) * (DH - 8));
          px(x, y, 1); px(x - 1, y, 1); px(x + 1, y, 1); px(x, y - 1, 1); px(x, y + 1, 1);
        }
      }
    }
    S.tint = 0;

    /* ---- reeded glass, with the seeded silhouette and any reaction behind it (visitors.js, reactions.js) ---- */
    // a bull or bear knock: the beast stands in its own beam of light, and plays its own reveal (beasts.js)
    const jitter = nervous ? (hash(fr, 11) - 0.5) * 3 : 0;
    // the Knocker (sprint 33 item 1, knocker.js): an alien's head becomes it on a plain, gold, red or costumed knock. Its dome sits
    // on the glass's foot on the desktop, and on the phone where the rule (or the sum) is taped across the glass; its fingers hook over that
    const kBase = P ? Math.min(GLASS.h, (rkind && PF.note.folded && !st.sum ? CY : PF.note.y) - GY) : GLASS.h;
    const gs = litLook
      ? litState({ look: litLook, kind: rkind, revealT, fr, t, outbreak, lamp, approach: step, jitter, eye, slip, redEyes: !!call?.punch && !!rkind && !st.reduced })
      : beast ? beastState({ beast, kind: rkind, revealT, fr, t, outbreak, lamp, approach: step, jitter, eye, slip, redEyes: !!call?.punch && !!rkind && !st.reduced })
      : glassState({
        sil: showVisitor ? silhouetteOf(st.visitor) : null, kind: rkind, verdict: reveal?.verdict ?? null, revealT,
        fr, t, outbreak, lamp, approach: step * (call?.lunge ?? 1) * lean, jitter, eye, slip,
        wrong: !st.reduced && rkind && (call?.flood || call?.scream) ? { flood: call.flood, scream: call.scream } : null, creep: last10 ? creepFrame(secsLeft) : null,
        redEyes: rkind === 'alien' && (call?.lunge ?? 1) > 1.2, // an alien lunging at the glass shows its red eyes at once
        knocker: { base: kBase, reduced: !!st.reduced },
      });
    // a wrong Open: the tilted OPENED. stamp in red, drawn in the glass where A3 has it (low, under the palms and the eyes), so the punch-in zooms it too
    // (on a phone out of the punch-in only the glass's top is in view, so palms, eyes and a stamp would overlap: there the small word stamp stays)
    // (the snap-back ends the zoom's flash and shake; the whole-glass view and its stamp stay until the card, so the small word stamp never sits across the mouth)
    const punching = P && (call?.punch || (call?.hold && st.phase !== 'reveal')) && rkind && !st.reduced;
    const art = call?.art && rkind && !st.reduced && showVisitor && st.phase !== 'reveal' && (!P || punching) ? stampArt() : null;
    if (art && (!gs.scream || gs.scream.f >= STAMP_FROM)) gs.stamp = true; // as in A3, the stamp lands once the scream's mouth has closed up above it
    drawGlass(B, GX, GY, gs);
    // what the glass painted is the glass's, not a plate's: toy town (toy.js) shows its own visitor there
    for (let v = 0; v < GH; v++) for (let u = 0; u < GW; u++) { const i = (GY + v) * SW + GX + u; if (GX + u >= 0 && GX + u < SW && GY + v >= 0 && GY + v < SH && !B.art[i]) B.art[i] = ART.GLASS; }
    // the phone's punch-in (red moment 2) zooms this glass, so it is kept before the door's notes and plates go over it
    const punch = punching ? punchGlass(gs) : null;
    const pv = punch ? punchView(SW) : null;
    for (const [x, y, w, h] of [[DX + 20, DY + 196, 86, 86], [DX + 122, DY + 196, 86, 86]]) {
      S.art = ART.TRIM; box(x, y, w, h, 1); box(x + 3, y + 3, w - 6, h - 6, 1);
      fill(x + 4, y + 4, w - 8, h - 8, (i, j) => { S.art = leafArt(i - DX, j - DY); return dz(i, j, 0.06); });
      S.art = 0;
    }
    const KX = DX + DW - 20, KY = DY + 194;
    S.art = ART.TRIM;
    for (let j = -7; j <= 7; j++) for (let i = -7; i <= 7; i++) { const d = Math.hypot(i, j); if (d <= 7) px(KX + i, KY + j, d > 6 ? 1 : dz(KX + i, KY + j, Math.max(0, 0.75 - Math.hypot(i - 2, j + 2) / 7))); }
    S.art = 0;

    /* ---- event plate on the glass, quarantine tape ---- */
    // a bull or bear plate stays up through the reaction too, in the knock's own light (never the outbreak's green)
    const plate = SP === T.gold ? 'Gold knock  x2 either way' : SP === T.red ? 'Red knock  x3 either way'
      : litLook ? litPlate(litLook) : beast === 'bull' ? 'Bull knock  x2 if right' : beast === 'bear' ? 'Bear knock  x2 if wrong' : outbreak ? 'Outbreak  x2'
      : st.oneKey ? (st.oneKey.held ? 'One Key night  1 key left' : 'Key spent  To shelter')
      : st.story ? `Night ${st.story.night}, door ${st.story.n} of ${st.story.of}  ${st.story.score ?? 0} pts` : ''; // a story door: where in the week you are, and the week's story points
    if (plate && !BLIND) {
      // phone: never under the menu bar when the top of the arch is cropped, nor under a lesson's header
      const pw0 = lw(plate, 'label') + 16, pxx = Math.round(GX + GW / 2 - pw0 / 2), pyy = P ? plateY(PF, !!st.coach && !st.coach.firstMinute, trayRows()) : GY + 10;
      publish('plate', JSON.stringify({ x: pxx, y: pyy, w: pw0 + 2, h: 20 })); // rows, shadow included: for the tests' overlap check
      fill(pxx + 2, pyy + 2, pw0, 18, 0);
      S.tint = SP || (litLook ? T[litLook] : beast ? T[beast] : outbreak ? T.green : 0); fill(pxx, pyy, pw0, 18, 1); S.tint = 0; // a One Key plate is plain paper
      box(pxx, pyy, pw0, 18, 0); label(plate, pxx + 8, pyy + 9, 'label', 0);
    }
    if (st.tallies) tallyPlate(st.tallies, now, 2, P ? plateY(PF, !!st.coach && !st.coach.firstMinute) + 22 : DY + TALLY_EYE); // desktop: down at the visitor's eye-line
    if (outbreak) {
      // phone: across the glass, above the rule; over a folded rule, low enough to clear a short visitor's eyes
      const ty = P ? PF.note.y - (PF.note.folded ? 6 : 16) : GY + GH + 8;
      fill(DX - 14, ty - 1, DW + 28, 12, 0);
      S.tint = T.green; fill(DX - 12, ty, DW + 24, 10, (x, y) => ((x + y) >> 2) & 1); S.tint = 0;
    }

    /* ---- tonight's rule, taped to the door ---- */
    // phone: drawn after the knocks, taped across the bottom of the glass (phone.js)
    const drawNote = (NX, NY, NW, lines) => {
      const lh = ruleLH, NH = Math.round(27 + lines.length * lh);
      words.cover(NX, NY, NW + 3, NH + 3); // the note hides a knock stamp under it (phone)
      fill(NX + 3, NY + 3, NW, NH, 0);
      fill(NX, NY, NW, NH, 1); box(NX, NY, NW, NH, 0);
      fill(NX, NY, NW, 18, 0); label("Tonight's rule", NX + 9, NY + 9, 'title', 1);
      // the body flashes inverted as the visitor steps up to the glass
      const flashed = showVisitor && st.phase === 'round' && ((roundT > 1.75 && roundT < 1.95) || (roundT > 2.1 && roundT < 2.3));
      lines.forEach((l, i) => say(l, NX + 9, Math.round(NY + 22 + i * lh), 'rule', flashed ? 1 : 0, 'left', S.clip, rulePx));
      if (flashed) xfill(NX + 1, NY + 19, NW - 2, NH - 20);
      for (const [tx, ty] of [[NX - 6, NY - 4], [NX + NW - 18, NY - 4]]) { fill(tx, ty, 24, 9, (x, y) => ((x + y) & 1)); box(tx, ty, 24, 9, 0); }
    };
    // after a verdict the sum is chalked where the rule hangs (the rule comes back with the next knock)
    if (st.sum && !P) chalkSum(st.sum, now, fr, { x: DX + 14, y: DY + 210, w: DW - 28, bottom: DY + DH - 6 }, true);
    else if (hasRule && !P) { if (fm) drawNote(DX + 8, DY + 216, DW - 16, [rule]); else drawNote(DX + 28, DY + 216, 172, words.wrap(rule, 'rule', 172 - 18).slice(0, 4)); }
    // phone, short window: the rule folded into a one-line strip, a "Rule" tab, the first words and an arrow
    const drawStrip = (NX, NY, NW) => {
      const NH = PHONE.foldH, tab = lw('Rule', 'title') + 12, room = NW - tab - 8 - 14;
      const first = words.wrap(rule, 'rule', room), line = first.length > 1 ? words.wrap(rule, 'rule', room - 10)[0] + '…' : first[0];
      words.cover(NX, NY, NW + 3, NH + 3);
      fill(NX + 3, NY + 3, NW, NH, 0);
      fill(NX, NY, NW, NH, 1); box(NX, NY, NW, NH, 0);
      fill(NX, NY, tab, NH, 0); label('Rule', NX + 6, NY + NH / 2, 'title', 1);
      const flashed = showVisitor && st.phase === 'round' && ((roundT > 1.75 && roundT < 1.95) || (roundT > 2.1 && roundT < 2.3));
      say(line, NX + tab + 5, NY + Math.round((NH - LH('rule')) / 2), 'rule', flashed ? 1 : 0);
      if (flashed) xfill(NX + tab, NY + 1, NW - tab - 1, NH - 2);
      for (let k = 0; k < 4; k++) hline(NX + NW - 9 - k, NY + 5 + k, 1 + 2 * k, flashed ? 1 : 0); // it opens upwards, over the glass
      for (const [tx, ty] of [[NX - 6, NY - 4], [NX + NW - 18, NY - 4]]) { fill(tx, ty, 24, 9, (x, y) => ((x + y) & 1)); box(tx, ty, 24, 9, 0); }
    };

    /* ---- the knock: stamped words on the door ---- */
    const KN = [[DX + 18, DY + 30], [DX + 118, DY + 66], [DX + 40, DY + 128]];
    if (st.round && st.phase === 'round') KNOCKS.forEach((k, i) => {
      if (roundT < k || roundT > 1.55) return;
      const p = ease((roundT - k) / 0.12), s = 'knock';
      const w = lw(s, 'stamp') + 14, h = 22, [x, y] = KN[i];
      if (p < 1) { const cxm = x + w / 2, cym = y + h / 2; xbox(cxm - w * p / 2, cym - h * p / 2, w * p, h * p); xbox(cxm - w * p / 4, cym - h * p / 4, w * p / 2, h * p / 2); return; }
      fill(x + 2, y + 2, w, h, 0); fill(x, y, w, h, 1); box(x, y, w, h, 0); label(s, x + 7, y + h / 2, 'stamp', 0);
    });
    /* ---- the call, stamped on the glass (call-lands.js): it lands with the ka-thunk and stays through the silence ---- */
    if (art) {
      if (!punch) coverClipped(GX + art.x + S.ox, GY + art.y, art.w + 3, art.h + 3); // only where the art can show: the phone's glass ends at the chat window's title
    } else if (call?.word && call.stamp > 0 && st.phase !== 'reveal' && showVisitor) {
      const w = lw(call.word, 'stamp') + 22, h = 28, x = Math.round(GX + GW / 2 - w / 2), y = P ? PF.note.y - h - 8 : GY + GH - h - 12;
      if (call.stamp < 1) { const cxm = x + w / 2, cym = y + h / 2, p = call.stamp; xbox(cxm - w * p / 2, cym - h * p / 2, w * p, h * p); xbox(cxm - w * p / 4, cym - h * p / 4, w * p / 2, h * p / 2); }
      else {
        words.cover(x, y, w + 3, h + 3);
        fill(x + 3, y + 3, w, h, 0); fill(x, y, w, h, 1); box(x, y, w, h, 0); box(x + 2, y + 2, w - 4, h - 4, 0); box(x + 3, y + 3, w - 6, h - 6, 0);
        label(call.word, x + w / 2, y + h / 2, 'stamp', 0, 'center');
      }
    }
    S.ox = 0;
    if (P) {
      S.clip = null;
      // a short phone (Safari with its bars) folds the rule into a one-line strip under the visitor's eyes. It
      // opens in full over the glass on a tap, and by itself while nobody is at the glass (the knocks, between
      // rounds). At the reveal it comes off the glass so the reaction shows down to the chat window
      const N = PF.note, fold = $('rulefold'), noteOff = (rkind && N.folded) || !!st.sum; // the sum is chalked where the rule hangs
      if (fold) tracked.add(fold); // hidden unless placed below
      const atGlass = showVisitor && !(st.phase === 'round' && roundT < 1.5);
      const unfold = N.folded && noteLines.length && !noteOff && (fold?.getAttribute('aria-expanded') === 'true' || !atGlass);
      const noteY = unfold ? N.y + N.h - N.full : N.y;
      if (noteLines.length && !noteOff) {
        if (!N.folded || unfold) drawNote(N.x, noteY, N.w, noteLines);
        else drawStrip(N.x, N.y, N.w);
        // the tap target: the strip and the tape above it (one touch row), or the whole open note
        if (N.folded && fold) place(fold, N.x, unfold ? noteY : N.y + N.h - PHONE.touch, N.w, unfold ? N.full : PHONE.touch);
      }
      const sumTop = st.sum ? chalkSum(st.sum, now, fr, { x: N.x, w: N.w, bottom: N.y + Math.max(N.h, PHONE.foldH) }, false) : null;
      // the Knocker's fingers hook over the sum's slab (or the rule bar), in front of it
      if (gs.knocker) { S.clip = [0, PF.menuH, SW, CY - PF.menuH]; knockerHands(B, GX, GY, gs, Math.min(GLASS.h, (sumTop ?? (noteOff ? CY : N.y)) - GY)); S.clip = null; }
      if (st.coach) { coachPhone(st.coach, st.coachOpen); }
      phoneMenu(st, flash);
      // the jack-o'-lantern stays in view while the sum is chalked where the rule hangs: it sits on the sum's slab instead (sprint 17)
      jackKnocker = !!gs.knocker;
      phoneJack($('start')?.classList.contains('on') || (noteOff && sumTop === null), sumTop ?? noteY);
      // the envelope's window keeps clear of the sum (or, before one, of where the rule hangs) and of the chat's title bar (sprint 29)
      fitMail(mailFit((sumTop ?? N.y) * PHONE.scale));
      publishBoxes(st);
    } else {
      fitMail(null, deskMailTop(st));
      publishBoxes(st);

      /* ================= menu bar ================= */
      fill(0, 0, SW, 17, 1); hline(0, 17, SW, 0);
      if ($('roompick')) tracked.add($('roompick')); // the desktop's way to the rooms is the start dialog's link row; this item is the phone menu's
      // a tiny door as the app icon
      fill(10, 3, 9, 12, 0); fill(12, 5, 5, 4, 1); px(17, 10, 1);
      let mx = 28;
      const f = flash ? 1 : 0, MY = 8.5; // the visual beep inverts the bar, so its words flip with it
      // the room tabs give way before they run into the items on the right: the night tab's short label first, then one word each
      const tabFull = b => (b.dataset.off ? OFF_TAB : MENU_LABEL[b.dataset.room] ?? b.textContent.trim());
      const tabSets = [tabFull, b => (b.dataset.off ? OFF_TAB_PHONE : tabFull(b)), b => shortRoomName(tabFull(b))];
      const roomBtns = [...doc.querySelectorAll('.rooms button')], tabsW = fn => roomBtns.reduce((n, b) => n + lw(fn(b), 'menu') + 16, mx);
      // the Discord link and the $KNOCK field (client/coin.js) at the right end. The bar is full at 640, so on a crowded bar things give way
      // in this order: the field's words (longest first), Discord to an icon, the Look label to "Look", the gaps between items, "Connect
      // wallet" to "Wallet", Copy into the field, and only then the tabs' names, as above. The shelter count keeps its room while it can.
      const discEl = $('discord'), coinEl = $('coin'), copyEl = $('coincopy'), lookOn = !!$('look') && chrome.look && !$('look').hidden;
      const discOn = !!discEl && chrome.discord !== false, coinOn = !!coinEl && chrome.coin !== false, mint = coinEl?.dataset.mint, copyOn = coinOn && !!mint && !!copyEl;
      const copyW = copyOn ? Math.max(lw(COPY_LABEL, 'menu'), lw(COPIED_LABEL, 'menu')) + 12 : 0;
      const textIds = ['roadmap', 'leaders', 'stairwell', 'wallet'].filter(id => chrome[id] && $(id) && !$(id).hidden).concat($('report') ? ['report'] : []);
      const walletName = short => (short && /^connect/i.test($('wallet').textContent.trim()) ? 'Wallet' : $('wallet').textContent.trim());
      const rightW = ({ g, icon, lookShort, walletShort, copyInField, coin }) => textIds.reduce((n, id) => n + lw(id === 'wallet' ? walletName(walletShort) : $(id).textContent.trim(), 'menu') + 12 + 2 * g, 2) + 20 + 2 * g + (chrome.logbook === false ? 0 : 18 + 2 * g) // + the mute icon, and the book
        + (lookOn ? lw(lookShort ? 'Look' : $('look').textContent.trim(), 'menu') + 12 + 2 * g : 0)
        + (discOn ? (icon ? 20 : lw(discEl.textContent.trim(), 'menu') + 12) + 2 * g : 0)
        + (coin ? lw(coin, 'menu') + 10 + 2 * g + (copyOn ? (copyInField ? 12 : copyW + 2 * g) : 0) : 0);
      const squeezes = [{ g: 4, icon: false, lookShort: false }, { g: 4, icon: true, lookShort: false }, { g: 4, icon: true, lookShort: true }, { g: 2, icon: true, lookShort: true }, { g: 2, icon: true, lookShort: true, walletShort: true }, { g: 2, icon: true, lookShort: true, walletShort: true, copyInField: true }];
      const plans = tabSets.flatMap(tab => squeezes.flatMap(q => (coinOn ? coinLabels(mint) : [null]).map(coin => ({ ...q, tab, coin }))));
      const barFits = (q, spare) => tabsW(q.tab) + rightW(q) + spare <= W - 6; // spare 56: the shelter at its shortest
      const plan = plans.find(q => barFits(q, 56)) ?? plans.find(q => barFits(q, 0)) ?? plans.at(-1), g = plan.g, tabName = plan.tab;
      for (const b of roomBtns) {
        if (!chrome.rooms) { tracked.add(b); continue; }
        // a shorter label on the bar; the button keeps its full name for screen readers
        const name = tabName(b), on = b.getAttribute('aria-pressed') === 'true', w = lw(name, 'menu') + 14;
        if (on) fill(mx, 0, w, 17, 0);
        label(name, mx + 7, MY, 'menu', (on ? 1 : 0) ^ f);
        place(b, mx, 0, w, 17);
        mx += w + 2;
      }
      let rx = W - 6;
      if (discEl && !discOn) tracked.add(discEl);
      else if (discEl && plan.icon) {
        // a speech bubble with three dots (the link keeps its "Discord" text for screen readers); not the service's own logo
        const w = 20, x0 = rx - w + 3;
        rx -= w; place(discEl, rx, 0, w, 17);
        box(x0, 3, 13, 9, 0); px(x0 + 2, 12, 0); px(x0 + 2, 13, 0); px(x0 + 1, 14, 0); px(x0 + 3, 12, 1);
        for (const dx of [3, 6, 9]) fill(x0 + dx, 7, 1, 1, 0);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      } else if (discEl) {
        const name = discEl.textContent.trim(), w = lw(name, 'menu') + 12;
        rx -= w; label(name, rx + 6, MY, 'menu', f); place(discEl, rx, 0, w, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      if (copyEl && (!copyOn || plan.copyInField)) tracked.add(copyEl); // on the fullest bar the field itself copies (placed below)
      else if (copyEl) {
        rx -= copyW; label(copyEl.textContent.trim(), rx + copyW / 2, MY, 'menu', f, 'center'); place(copyEl, rx, 0, copyW, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      if (plan.coin) {
        // a field: the words in a box, a value to read, not a button
        const inField = copyOn && plan.copyInField, w = lw(plan.coin, 'menu') + 10 + (inField ? 12 : 0);
        rx -= w; box(rx, 2, w, 14, 0); label(plan.coin, rx + 5, MY, 'menu', f);
        if (inField) { // Copy as a glyph at the field's end (two sheets), and the whole field copies
          const cx = rx + w - 14; box(cx, 4, 7, 7, 0); fill(cx + 3, 7, 7, 7, 1); box(cx + 3, 7, 7, 7, 0);
          place(copyEl, rx, 0, w, 17);
        }
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const roadEl = $('roadmap');
      if (roadEl && !chrome.roadmap) tracked.add(roadEl);
      else if (roadEl) {
        const name = roadEl.textContent.trim(), w = lw(name, 'menu') + 12;
        rx -= w; label(name, rx + 6, MY, 'menu', f); place(roadEl, rx, 0, w, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const leadEl = $('leaders');
      if (leadEl && !chrome.leaders) tracked.add(leadEl);
      else if (leadEl) {
        const name = leadEl.textContent.trim(), w = lw(name, 'menu') + 12;
        rx -= w; label(name, rx + 6, MY, 'menu', f); place(leadEl, rx, 0, w, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const bookEl = $('logbook');
      if (bookEl && chrome.logbook === false) tracked.add(bookEl);
      else if (bookEl) {
        // a book (the link keeps its "Logbook" text for screen readers): two pages open on a spine
        const w = 18, x0 = rx - w + 3;
        rx -= w; place(bookEl, rx, 0, w, 17);
        box(x0, 4, 6, 9, 0); box(x0 + 6, 4, 6, 9, 0); vline(x0 + 6, 3, 11, 0);
        fill(x0 + 1, 7, 4, 1, 0); fill(x0 + 7, 7, 4, 1, 0); fill(x0 + 1, 10, 4, 1, 0); fill(x0 + 7, 10, 4, 1, 0); // its lines
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const stairEl = $('stairwell');
      if (stairEl && !chrome.stairwell) tracked.add(stairEl);
      else if (stairEl) {
        const name = stairEl.textContent.trim(), w = lw(name, 'menu') + 12;
        rx -= w; label(name, rx + 6, MY, 'menu', f); place(stairEl, rx, 0, w, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const repEl = $('report');
      if (repEl) {
        const name = repEl.textContent.trim(), w = lw(name, 'menu') + 12;
        rx -= w; label(name, rx + 6, MY, 'menu', f); place(repEl, rx, 0, w, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const muteEl = $('mute');
      if (muteEl) {
        // a speaker icon (the button keeps its "Sound: on/off" text for screen readers): waves when on, a cross when off
        const w = 20, x0 = rx - w + 2;
        rx -= w; place(muteEl, rx, 0, w, 17);
        fill(x0 + 2, 7, 3, 4, 0); fill(x0 + 5, 6, 1, 6, 0); fill(x0 + 6, 5, 1, 8, 0); fill(x0 + 7, 4, 1, 10, 0);
        if (muteEl.getAttribute('aria-pressed') === 'true') for (let i = 0; i < 5; i++) { px(x0 + 10 + i, 6 + i, 0); px(x0 + 14 - i, 6 + i, 0); }
        else { vline(x0 + 10, 6, 5, 0); vline(x0 + 12, 4, 9, 0); }
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const lookEl = $('look');
      if (lookEl && (!chrome.look || lookEl.hidden)) tracked.add(lookEl); // hidden: the public game's one look (look.js fixLook)
      else if (lookEl) {
        const name = plan.lookShort ? 'Look' : lookEl.textContent.trim(), w = lw(name, 'menu') + 12;
        rx -= w; label(name, rx + 6, MY, 'menu', f); place(lookEl, rx, 0, w, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g;
      }
      const wal = $('wallet');
      if (wal && !chrome.wallet) tracked.add(wal);
      else if (wal) {
        const name = walletName(plan.walletShort), w = lw(name, 'menu') + 12;
        rx -= w; label(name, rx + 6, MY, 'menu', f); place(wal, rx, 0, w, 17);
        rx -= g; vline(rx, 3, 11, 0); rx -= g + 2;
      }
      // shelter: one heart per flat, squeezed if the bar is short of room
      const left = st.shelter?.left ?? 10, total = st.shelter?.total ?? 10;
      // "7/10 safe homes" when it fits; on a crowded bar "7/10" with a heart in front, so it never reads as a door count
      const full = `${left}/${total} safe homes`, fits = lw(full, 'menu') <= rx - mx - 6;
      const count = fits ? full : `${left}/${total}`;
      // not before the visitor's first door of their own (sprint 31), and never over the room tabs when even the short count has no room
      const showHouses = st.houses !== false && (fits || lw(count, 'menu') + 14 <= rx - mx - 6);
      const cw = showHouses ? Math.ceil(label(count, rx, MY, 'menu', f, 'right')) : 0;
      if (showHouses && !fits) heart(rx - cw - 12, 4, left > 0);
      if (showHouses) rx -= cw + (fits ? 5 : 14);
      const room = rx - (mx + 8), stepX = showHouses ? Math.min(11, Math.floor((room + 2) / Math.max(1, total))) : 0;
      if (stepX >= 10) { const hx0 = rx - total * stepX + 2; for (let k = 0; k < total; k++) heart(hx0 + k * stepX, 4, k < left); }
      else if (stepX >= 6) {
        const hx0 = rx - total * stepX + 1;
        for (let k = 0; k < total; k++) { const x = hx0 + k * stepX; if (k < left) { fill(x, 6, 2, 2, 0); fill(x + 3, 6, 2, 2, 0); fill(x, 8, 5, 2, 0); fill(x + 1, 10, 3, 1, 0); px(x + 2, 11, 0); } else { px(x, 6, 0); px(x + 4, 6, 0); px(x, 8, 0); px(x + 4, 8, 0); px(x + 1, 10, 0); px(x + 3, 10, 0); px(x + 2, 11, 0); } }
      }
      if (flash) xfill(0, 0, SW, 17); // the visual beep: the menu bar flashes on each knock
    }

    /* ================= chat window ================= */
    const r = st.round;
    const title = st.role === 'doorkeeper' && r && st.phase !== 'start' && st.phase !== 'queued' ? r.visitorName
      : st.role === 'visitor' && r && st.phase !== 'start' && st.phase !== 'queued' ? 'Behind the door' : 'The door';
    windowFrame(CX, CY, CW, CH, title);
    // phone: every control one touch row high, the timer a bar under the title (phone.js)
    const BY = P ? PF.buttons.y : CY + CH - 27, TY = BY - 22, CHH = P ? PF.touch : 15, ASKH = P ? PF.touch : 16;
    const chips = [...($('chips')?.children ?? [])];
    let chipsY = P ? (chips.length ? PF.chips.y : BY - 6) : TY - 9 - (chips.length ? CHH : 0);
    const askY = chipsY - (chips.length ? 4 : 0) - ASKH;
    // phone: outside a round the wait line sits just above the talk field, so the messages stop above it
    // the knocker's Brief button (the way back to the inspector brief): on a phone it takes the whole button row, so the wait line moves up too
    const bb = $('briefbtn'), showBb = !!bb && !bb.hidden && !st.brief && st.role === 'visitor' && inRound;
    const waitRow = P && ((!inRound && (st.phase === 'start' || st.phase === 'queued')) || (showBb && inRound)) ? Math.round(LH('chat')) + 6 : 0;
    let msgTop = P ? PF.msgs.y : CY + 20, msgBot = askY - 5 - waitRow;
    // phone: the start dialog and the verdict alert sit over the chat window; the messages keep to the room they
    // leave, whole lines only, so no half sentence or bubble edge shows round them
    const start = $('start'), rev = $('reveal');
    const startBox = P && !st.brief && start?.classList.contains('on') ? phoneStartBox() : null; // the inspector brief takes the dialog's place
    if (startBox) msgTop = Math.max(msgTop, startBox.y0 + startBox.h + 3 + 5);
    if (pv) msgTop = Math.max(msgTop, pv.rows + 5); // the punched glass lies over the chat's top: a bubble it would cut is left out whole
    const al = rev?.classList.contains('on') && st.reveal?.view ? alertBox(st.reveal.view, st.role === 'doorkeeper' ? st.card : null) : null;
    if (al && (P || st.reveal.view.slip || al.card)) msgBot = Math.min(msgBot, al.AY - 5);
    // the refusal notice (a black bar just over the talk field) takes its own room, so the messages end above it
    const refusal = ($('notice')?.textContent ?? '').trim();
    const barLines = refusal ? words.wrap(refusal, 'chat', CW - 34) : [], barH = barLines.length ? Math.round(barLines.length * LH('chat')) + 3 : 0;
    if (barH) msgBot -= barH + 3;

    // messages, newest at the bottom
    const clh = LH('chat'), BUB = PHONE.bubble;
    let typingOn = false;
    const items = [...($('chat')?.children ?? [])].map(el => {
      const cls = el.className, txt = el.textContent;
      if (!seen.has(el)) seen.set(el, firstDraw ? now - 1000 : now); // history on load does not zoom in
      const typing = cls.includes('typing');
      if (typing) { typingOn = true; return P ? null : { el, kind: 'typing', h: 20, gap: BUB.gap.typing }; }
      if (cls === 'me' || cls === 'them') {
        // phone: a bubble may run to 30 rows short of the window, so a short question stays on one line at 375
        const lines = words.wrap(txt, 'chat', P ? CW - 46 : 232); const w = Math.ceil(Math.max(...lines.map(l => words.measure(l, 'chat')))) + 16;
        return { el, kind: cls, lines, w, h: Math.round(lines.length * clh) + BUB.pad, gap: PHONE.bubble.gap[cls], slip: !!el.dataset.slip };
      }
      const lines = words.wrap(txt, 'chat', CW - 34), kind = cls === 'err' ? 'err' : 'sys';
      return { el, kind, lines, h: Math.round(lines.length * clh) + 3, gap: BUB.gap[kind] };
    }).filter(Boolean);
    // the slip (roadmap 30): while the verdict alert is up, the chat scrolls back so the slipped line is the lowest shown
    const slipAt = al && st.reveal.view.slip ? items.findLastIndex(it => it.slip) : -1;
    if (slipAt >= 0) items.length = slipAt + 1;
    S.clip = [CX + 1, msgTop, CW - 2, Math.max(0, msgBot - msgTop)];
    // phone: the newest message sits right on the bottom (no gap under it), to keep room for the one before
    let y = msgBot + (P && items.length ? items[items.length - 1].gap : 0), drawnLast = null, drawnKinds = [];
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i];
      y -= it.h + it.gap;
      // older lines that do not fit whole are left out, not drawn as slivers; on a phone the newest too
      if (y < msgTop && (P || i < items.length - 1)) break;
      if (drawnLast === null && it.lines) drawnLast = it.lines.join(' ');
      if (it.lines) drawnKinds.unshift(it.kind);
      const p = (now - seen.get(it.el)) / 250;
      if (it.kind === 'typing') {
        bubble(CX + 12, y, 38, 20, false);
        const a = fr % 9 / 3 | 0;
        for (let k = 0; k < 3; k++) { if (k === a) fill(CX + 22 + k * 6, y + 8, 3, 4, 0); else box(CX + 22 + k * 6, y + 9, 3, 3, 0); }
      } else if (it.kind === 'me' || it.kind === 'them') {
        const bx = it.kind === 'me' ? CX + CW - 14 - it.w : CX + 12;
        const from = [it.kind === 'me' ? CX + CW - 52 : CX + 12, y, 38, it.h];
        if (p < 1 && p >= 0) zoom(from, [bx, y, it.w, it.h], p);
        else {
          if (it.slip) slipMark(bx, y, it.w, it.h, it.kind === 'me', CX + 1, CX + CW - 1);
          const fg = bubble(bx, y, it.w, it.h, it.kind === 'me'); it.lines.forEach((l, j) => say(l, bx + 8, Math.round(y + 3 + j * clh), 'chat', fg)); }
      } else if (it.kind === 'err') {
        fill(CX + 10, y, CW - 20, it.h, 0);
        it.lines.forEach((l, j) => say(l, CX + 16, Math.round(y + 1 + j * clh), 'chat', 1));
      } else {
        // system lines: plain print between dotted rules
        dotted(CX + 10, y - 2, CW - 20, 0);
        it.lines.forEach((l, j) => say(l, CX + 14, Math.round(y + 1 + j * clh), 'chat', 0));
      }
    }
    S.clip = null;
    const coachUp = P && st.coach && st.coachOpen;
    if (coachUp) coachBox(st.coach, msgTop, msgBot); // a lesson's text, until the first question (never over the glass)
    else publish('coach', null);
    publish('chatLast', drawnLast);
    publish('chatKinds', drawnKinds.join(',')); // the bubbles drawn whole, oldest first (the phone checks: the first minute's question sits above the reply)

    // talk field and question chips
    const ask = $('ask');
    if (ask) field(ask, CX + 10, askY, CW - 20, ASKH, ask.placeholder || 'talk through the door', now, 'chat');
    // the refusal notice: a black bar just over the talk field, not a chat message (the box keeps the sender's text)
    if (barH) {
      const ny = askY - 3 - barH;
      fill(CX + 10, ny, CW - 20, barH, 0);
      barLines.forEach((l, j) => say(l, CX + 16, Math.round(ny + 1 + j * clh), 'chat', 1));
    }
    let cxp = CX + 10;
    for (const b of chips) {
      // a question chip is a line you can send, so it is sentence text too
      const label = b.textContent, w = Math.ceil(words.measure(label, 'chat')) + 12;
      if (cxp + w > CX + CW - 10) { tracked.add(b); continue; } // no room: hidden this frame
      fill(cxp + 1, chipsY, w - 2, CHH, 1); hline(cxp + 1, chipsY, w - 2, 0); hline(cxp + 1, chipsY + CHH - 1, w - 2, 0); vline(cxp, chipsY + 1, CHH - 2, 0); vline(cxp + w - 1, chipsY + 1, CHH - 2, 0);
      say(label, cxp + 6, chipsY + Math.round((CHH - clh) / 2), 'chat', 0);
      place(b, cxp, chipsY, w, CHH);
      cxp += w + 5;
    }

    // timer
    if (P && typingOn) phoneTyping(title, fr, inRound ? CX + CW - tw(String(secsLeft), 'mid') - 18 : CX + CW - 3);
    if (P) phoneTimer(inRound, secsLeft, nervous, SP, outbreak, fr, st.phase, askY - 5, roundLen, last10);
    else {
    hline(CX + 1, TY - 6, CW - 2, 0);
    if (inRound) {
      let nW; // the countdown: a big number, pixel font; red on an ink tab in the last 10 s of a plain or gold door (red moment 1)
      if (last10) { nW = tw(String(secsLeft), 'big'); fill(CX + 8, TY - 5, nW + 8, 20, 0); S.tint = T.red; text(String(secsLeft), CX + 12, TY - 3, 'big', 1); S.tint = 0; }
      else nW = text(String(secsLeft), CX + 12, TY - 3, 'big', 0);
      const flash = nervous && fr % 4 < 2, fx0 = last10 ? CX + 17 + nW : CX + 1; // the flash leaves the red digits' tab alone
      label(secsLeft === 1 ? 'second left' : 'seconds left', CX + 18 + nW, TY + 7, 'line', flash ? 1 : 0); // flips with the flash below
      const barW = 182, barX = CX + CW - 12 - barW;
      box(barX, TY + 1, barW, 12, 0);
      for (let k = 0; k < 60; k++) { const x = barX + 2 + k * 3; if (k < barTicks(secsLeft, roundLen)) fill(x, TY + 3, 2, 8, 0); else fill(x, TY + 3, 2, 8, (i, j) => (((i + j) & 1) && (j & 1) ? 0 : 1)); }
      if (flash) {
        xfill(fx0, TY - 5, CX + CW - 1 - fx0, 20);
        if (SP || outbreak) for (let j = TY - 5; j < TY + 15; j++) for (let i = fx0; i < CX + CW - 1; i++) if (B.bits[j * SW + i]) B.tint[j * SW + i] = SP || T.green;
      }
    } else {
      const msg = st.phase === 'queued' ? 'waiting for a knock' : st.phase === 'start' ? 'no knock yet' : '';
      if (msg) say(msg, CX + 12, TY + Math.round((15 - clh) / 2), 'chat', 0);
    }
    }

    // decision buttons, or the visitor's wait line
    const open = $('open'), shut = $('shut');
    // phone: no key caps (no keyboard), two equal buttons
    if (open && !open.hidden) button(open, P ? PF.open.x : CX + 16, BY, P ? PF.open.w : 120, open.dataset.away === '1' ? 'To shelter' : 'Open', P ? '' : 'A', false, open.disabled); // One Key: the spent key's To shelter
    if (shut && !shut.hidden) button(shut, P ? PF.shut.x : CX + CW - 16 - 140, BY, P ? PF.shut.w : 140, 'Keep shut', P ? '' : 'D', true, shut.disabled);
    const wait = ($('wait')?.textContent ?? '').trim();
    if (showBb) { const bx0 = P ? PF.open.x : CX + CW - 16 - 64; button(bb, bx0, BY, P ? PF.shut.x + PF.shut.w - bx0 : 64, 'Brief', '', false, false); }
    if (wait && open?.hidden) {
      if (P && showBb) say(wait, CX + 12, Math.round(askY - 5 - clh - 2), 'chat', 0); // phone: above the talk field, the button has the row
      else say(wait, showBb ? CX + 14 : CX + CW / 2, BY + Math.round(((P ? PF.touch : 20) - clh) / 2), 'chat', 0, showBb ? 'left' : 'center');
    }

    /* ================= this shift ================= */
    const clock = ($('clock')?.textContent ?? '').trim();
    const startUp = !!start?.classList.contains('on') && !st.brief;
    if (P) { if (PF.hour) phoneHour(st, clock, startUp); } else if (st.coach || st.coachBetween) coachDesk(st.coach ?? st.coachBetween, say, !st.coach); else if (nightOff()) offHour(!startUp || teaserDropped); else {
      windowFrame(IX, IY, IW, IH, st.shiftTitle ?? 'This shift');
      let iy = IY + 19;
      const sol = (Number(st.prizePoolLamports ?? 0) / 1e9).toFixed(2);
      const pw = text(`${sol} SOL`, IX + 12, iy, 'big', 0);
      const blh = LH('board'), rlh = LH('receipt');
      label(SHIFT_POOL_NOTE, IX + 20 + pw, iy + 9, 'line', 0);
      iy += 19; hline(IX + 1, iy, IW - 2, 0);
      const foot = IY + IH - 14; // the disclaimer strip starts here
      // notice board: the top three and you, in sentence text
      const LX0 = IX + 10, LW = 136;
      const rows = [...($('board')?.children ?? [])];
      if (!rows.length) say(($('board')?.textContent ?? '').trim() || 'No scores this shift yet.', LX0, iy + 3, 'board', 0);
      rows.slice(0, 4).forEach((row, i) => {
        const yy = Math.round(iy + 2 + i * blh), me = row.classList.contains('you');
        const [n, p] = [...row.children].map(s => s.textContent);
        if (me) fill(LX0 - 4, yy, LW + 8, Math.round(blh), 0);
        const name = n.length > 18 ? n.slice(0, 17) + '…' : n;
        say(name, LX0, yy, 'board', me ? 1 : 0); say(String(p), LX0 + LW, yy, 'board', me ? 1 : 0, 'right');
      });
      vline(IX + 156, iy + 1, foot - iy - 1, 0);
      // receipt: the payout ledger
      const GX0 = IX + 164, GW0 = IW - 174;
      let gy = iy + 2;
      label('Payout ledger', GX0, gy + 6, 'title', 0); gy += 14;
      const ledger = [...($('receipt')?.children ?? [])].filter(a => a.tagName === 'A');
      if (!ledger.length) { say(($('receipt')?.textContent ?? '').trim() || 'No payouts yet.', GX0, gy, 'receipt', 0); gy += rlh; }
      for (const a of ledger.slice(0, 2)) {
        const [l, rr] = [...a.children].map(s => s.textContent);
        say(l, GX0, gy, 'receipt', 0); say(rr + ' SOL', GX0 + GW0, gy, 'receipt', 0, 'right');
        dotted(GX0, Math.round(gy + rlh), GW0, 0);
        place(a, GX0, gy, GW0, Math.round(rlh));
        gy = Math.round(gy + rlh + 1);
      }
      if (st.prizePct && gy + rlh <= foot) { say('split', GX0, gy, 'receipt', 0); say(`${st.prizePct}% of fees to prizes`, GX0 + GW0, gy, 'receipt', 0, 'right'); }
      hline(IX + 1, foot, IW - 2, 0);
      say(DISCLAIMER, IX + IW / 2, foot + Math.round((IY + IH - 1 - foot - LH('print')) / 2), 'print', 0, 'center');
    }

    if (!P && st.shiftTotal != null) shiftCorner(st.shiftTotal, 4, 339);

    /* ================= start dialog (over the door) ================= */
    let startFrame = null;
    if (startBox) { phoneStart(start, now, say, startBox); startFrame = [startBox.x, startBox.y0, startBox.w + 2, startBox.h + 2]; }
    else if (start?.classList.contains('on') && !st.brief) {
      // the story (index.html #story) opens the dialog, in sentence text; the dialog grows with it
      const story = startStory(DW + 44 - 32), blh2 = LH('board');
      const x = DX - 22, y0 = 36 + bannerShift(36), w = DW + 44, top = BRAND_H + 22 + story.h + 8;
      // one plain sentence for would-be inspectors (index.html #plain), in sentence text under the role buttons
      const plain = ($('plain')?.textContent ?? '').replace(/\s+/g, ' ').trim(), plainLines = words.wrap(plain, 'board', w - 32);
      const noteLines = startNoteLines(w - 32);
      const nS = noteLines.length ? Math.round(noteLines.length * blh2) + 4 : 0; // its own lines between the name field and the buttons
      const plainH = Math.round(plainLines.length * blh2), rest = top + 80 + nS + plainH + 4 + startLinksH();
      // a short window: the night shift card goes, so the links stay on screen (the Night shift window beside the door says the same)
      let tv = teaserView(w - 32);
      const dropCard = !!tv && nightOff() && y0 + rest + tv.h + 6 + 2 > SH;
      teaserDropped = dropCard;
      if (dropCard) tv = null;
      const midH = tv ? tv.h + 6 : dropCard ? 4 : 18, h = rest + midH;
      words.cover(x, y0, w + 2, h + 2);
      windowFrame(x, y0, w, h, 'Pick a side');
      brandHead(x + 16, y0 + 21, say);
      const badge = tonightBadge(x + 16, y0 + 21, x + w - 16);
      story.head.forEach((l, i) => say(l, x + 16, Math.round(y0 + 21 + BRAND_H + i * story.hlh), 'label', 0));
      story.body.forEach((l, i) => say(l, x + 16, Math.round(y0 + 21 + BRAND_H + story.head.length * story.hlh + 4 + i * story.blh), 'board', 0));
      label('Your name', x + 16, y0 + top + 7, 'label', 0);
      field($('name'), x + 16, y0 + top + 17, w - 32, 18, 'your name', now, 'chat');
      const [dk, vs] = start.querySelectorAll('button');
      // the second label is the longer one: it gets the wider button so it does not run past its edges
      noteLines.forEach((l, i) => say(l, x + 16, Math.round(y0 + top + 41 + i * blh2), 'board', 0));
      // a used-up side is drawn dotted and loses the default ring to the open one
      if (dk) button(dk, x + 16, y0 + top + 50 + nS, 92, dk.textContent.trim(), '', !dk.disabled, dk.disabled);
      if (vs) button(vs, x + 118, y0 + top + 50 + nS, w - 134, vs.textContent.trim(), '', dk?.disabled && !vs.disabled, vs.disabled);
      plainLines.forEach((l, i) => say(l, x + 16, Math.round(y0 + top + 76 + nS + i * blh2), 'board', 0));
      if (tv) drawTeaser(tv, x + 16, y0 + top + 80 + nS + plainH + 4, w - 32, say);
      else if (!dropCard) say('Practice is free. Night shift: hold KNOCK.', x + w / 2, y0 + top + 80 + nS + plainH + 4, 'print', 0, 'center');
      startLinks(say, x + 16, y0 + top + 80 + nS + plainH + 4 + midH + 2, 0, badge ? 'tonightlink' : null);
      startFrame = [x, y0, w + 2, h + 2];
    }
    publishStart(startFrame);

    /* ================= verdict alert (over the chat) ================= */
    // reduced motion: the alert (or the card) is there in its last frame at once, no zoom rectangles (QA bug 1)
    const alertP = () => {
      const alertT = st.alertAt != null ? Math.floor((now - st.alertAt) / 1000 * FPS) / FPS : revealT;
      return st.cardReduced || st.reduced ? 1 : alertT >= 0 ? alertT / 0.28 : 1;
    };
    // a tag after the points, in the special's light (gold, red, a beast's, an outbreak's green)
    const multTag = (v, x, cy) => {
      if (!(v.mult > 1)) return;
      const tag = `x${v.mult}`, tgw = lw(tag, 'tag') + 10, ty = Math.round(cy - 6.5);
      S.tint = multTagTint(v);
      fill(x, ty, tgw, 13, 1); S.tint = 0;
      box(x, ty, tgw, 13, 0); label(tag, x + tgw / 2, ty + 6.5, 'tag', 0, 'center');
    };
    /* the reveal card (reveal-card.js, sprint 27 item 3) with the verdict merged in (fix pass): one window in the
       alert's place, never over the glass, so the reveal on the glass stays the payoff. It zooms out of the glass in four
       steps (at once with reduced motion). Doorkeepers only; a knocker gets the alert below. */
    if (al?.card) {
      const v = st.reveal.view, { lay, AW, AH, AX, AY } = al, p = alertP();
      if (p < 1) zoom([GX, GY, GW, P ? PF.glass.shown : GH], [AX, AY, AW, AH], Math.max(0, p));
      else {
        // a card that took part of the chat window's title row takes all of it, so no cut-off name bar shows above it
        if (AY < CY + 18) { words.cover(CX, CY, CW + 2, AY - CY); fill(CX, CY, CW + 2, AY - CY, 0); }
        const at = drawCard(B, words, AX, AY, st.card, lay, S.ox);
        framesDrawn.push([AX, AY, AW + 3, AH + 3]); // a window too, for toy town's title bar (toy.js)
        multTag(v, at.tagX, at.tagY);
        const nx = $('next'), vb = P ? verdictButtons(AW) : null, by2 = AY + AH - (P ? PHONE.touch + 10 : 30);
        const ag = v.again && $('ano') ? againLayout(AX, AW, by2) : null;
        if (nx) button(nx, ag ? ag.nx : P ? AX + vb.next.x : AX + AW - 112, by2, ag ? ag.nw : P ? vb.next.w : 96, nx.textContent.trim(), '', true, false);
        if (ag) { // the question on its own line, the second answer beside the first
          label('Play this again?', AX + 12, ag.qy, 'label', 0);
          button($('ano'), ag.bx, by2, ag.bw, $('ano').textContent.trim(), '', false, false);
        }
        const sk = $('coachskip'); // between practice lessons: leave them for good
        if (sk && !sk.hidden) button(sk, P ? AX + vb.skip.x : AX + 12, by2, P ? vb.skip.w : 120, P ? 'Skip' : 'Skip lessons', '', false, false);
        const sh = $('share'); // an end screen: Share takes Skip's place (Skip shows only between two lessons)
        if (sh && !sh.hidden) {
          if (ag) button(sh, ag.sx, ag.sy, ag.sw, sh.textContent.trim(), '', false, false);
          else button(sh, P ? AX + vb.skip.x : AX + 12, by2, P ? vb.skip.w : 120, sh.textContent.trim(), '', false, false);
        }
      }
    } else if (al) {
      const v = st.reveal.view, { headLines, verdictLines, headH, noteY, AW, AH, AX, AY } = al, vlh = LH('verdict');
      const p = alertP();
      const shutR = P ? [PF.shut.x, BY, PF.shut.w, PF.touch] : [CX + CW - 156, BY, 140, 20];
      if (p < 1) zoom(shutR, [AX, AY, AW, AH], p);
      else {
        words.cover(AX, AY, AW + 3, AH + 3); // the alert hides the chat lines under it
        fill(AX + 3, AY + 3, AW, AH, 0);
        fill(AX, AY, AW, AH, 1); box(AX, AY, AW, AH, 0); box(AX + 3, AY + 3, AW - 6, AH - 6, 0); box(AX + 4, AY + 4, AW - 8, AH - 8, 0);
        // icon: the glass arch with who was there
        mugshot(B, AX + 14, AY + 14, v.kind); // the same portrait as the reveal card's (reveal-card.js)
        headLines.forEach((l, i) => label(l, AX + 52, AY + 18 + i * 14, 'title', 0));
        const pts = `${v.points > 0 ? '+' : v.points < 0 ? '-' : ''}${Math.abs(v.points)}`;
        const pw1 = Math.ceil(label(pts, AX + 52, AY + 37 + headH, 'points', 0));
        if (v.mult > 1) {
          const tag = `x${v.mult}`, tgw = lw(tag, 'tag') + 10;
          S.tint = multTagTint(v);
          fill(AX + 58 + pw1, AY + 30 + headH, tgw, 13, 1); S.tint = 0;
          box(AX + 58 + pw1, AY + 30 + headH, tgw, 13, 0); label(tag, AX + 58 + pw1 + tgw / 2, AY + 36.5 + headH, 'tag', 0, 'center');
        }
        verdictLines.forEach((l, i) => say(l, AX + (P ? 14 : 52), Math.round(AY + noteY + i * vlh), 'verdict', 0));
        const nx = $('next'), vb = P ? verdictButtons(AW) : null, by2 = AY + AH - (P ? PHONE.touch + 10 : 30);
        if (nx) button(nx, P ? AX + vb.next.x : AX + AW - 112, by2, P ? vb.next.w : 96, nx.textContent.trim(), '', true, false);
        const sk = $('coachskip'); // between practice lessons: leave them for good
        if (sk && !sk.hidden) button(sk, P ? AX + vb.skip.x : AX + 52, by2, P ? vb.skip.w : 120, P ? 'Skip' : 'Skip lessons', '', false, false);
      }
    }

    /* ================= shift wait (all the shift's doors played; over the chat) ================= */
    if (st.phase === 'wait' && st.wait) {
      const secs = Math.max(0, st.wait.left ?? st.wait.seconds ?? 0); // counted from the seconds the server sent
      const head = `Next shift in ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
      const clock = head.slice('Next shift in '.length);
      const lines = words.wrap(`You have played all ${st.wait.doors ?? 10} doors of this shift${st.wait.sidesLine ? ` (${st.wait.sidesLine})` : ''}. The practice room is open now.`, 'coach', CW - 20 - 28);
      const wlh = LH('coach'), hh = 20, bx = CX + 10, bw = CW - 20, by = CY + 26, bh = Math.round(18 + hh + 10 + lines.length * wlh + 12);
      words.cover(bx, by, bw + 3, bh + 3);
      fill(bx + 3, by + 3, bw, bh, 0); fill(bx, by, bw, bh, 1); box(bx, by, bw, bh, 0); box(bx + 3, by + 3, bw - 6, bh - 6, 0);
      // the words in the sentence font, the countdown itself a big number in the pixel font
      const hw = Math.ceil(label('Next shift in', bx + 14, by + 10 + hh / 2, 'title', 0));
      text(clock, bx + 14 + hw + 6, by + 10, 'big', 0);
      hline(bx + 14, by + 10 + hh + 4, bw - 28, 0);
      lines.forEach((l, i) => say(l, bx + 14, Math.round(by + 10 + hh + 10 + i * wlh), 'coach', 0));
    }

    /* ================= inspector brief (sprint 15, roadmap 25; game/tutorial.js INSPECTOR_BRIEF) =================
       Over everything but the menu: the first line plain, the points after it each with a square bullet, one button. */
    if (st.brief) {
      const bf = st.brief, bl = LH('board'), T2 = P ? PHONE.touch : 20, bgap = 4;
      const bx = P ? 4 : DX - 22, by0 = P ? PHONE.menuH + 4 : 36, bw = P ? SW - 8 : DW + 44;
      const rows = bf.lines.map((l, i) => words.wrap(l, 'board', bw - 32 - (i ? 10 : 0)));
      const bh = Math.round(24 + rows.reduce((n, r) => n + r.length, 0) * bl + (rows.length - 1) * bgap + 10 + T2 + 14);
      words.cover(bx, by0, bw + 3, bh + 3);
      windowFrame(bx, by0, bw, bh, bf.title);
      let yy = by0 + 24;
      rows.forEach((r, i) => {
        if (i) fill(bx + 16, Math.round(yy + bl / 2 - 2), 4, 4, 0);
        r.forEach((l, j) => say(l, bx + 16 + (i ? 10 : 0), Math.round(yy + j * bl), 'board', 0));
        yy += r.length * bl + bgap;
      });
      button($('briefok'), bx + bw - 16 - 96, by0 + bh - T2 - 12, 96, bf.ok, '', true, false);
    }

    // red moment 2, the phone's punch-in: from the impact to the end of the freeze the glass is blown up 2x over the top 60% of the window
    // (a cropped view of the middle of it: 2x of a 180 px glass is wider than a phone), the stamp on it; then it snaps back
    if (punch) {
      const rows = Math.min(SH, pv.rows);
      words.cover(0, 0, SW, rows + 1);
      S.ox = 0; S.clip = null;
      for (let y = 0; y < rows; y++) {
        const v = pv.v(y);
        for (let x = 0; x < SW; x++) {
          const u = pv.u(x), on = archAt(u, v), i = (punch.oy + v) * punch.B.w + punch.ox + u;
          S.tint = on ? punch.B.tint[i] : 0; S.art = on ? punch.B.art[i] : 0; px(x, y, on ? punch.B.bits[i] : 0);
        }
      }
      S.tint = 0; S.art = 0;
      hline(0, rows, SW, 1);
    }

    if (P) phoneMenuDrop();

    /* ---- keyboard focus: a dotted ring around the focused control ---- */
    const fe = doc.activeElement, fr8 = fe && placed.get(fe);
    if (fr8 && fe.matches(':focus-visible, input')) { const [x, y2, w, h] = fr8; for (let i = -3; i < w + 3; i += 2) { px(x + i, y2 - 3, 0); px(x + i, y2 + h + 2, 0); } for (let j = -3; j < h + 3; j += 2) { px(x - 3, y2 + j, 0); px(x + w + 2, y2 + j, 0); } }

    // a wrong Open's flash (call-lands.js): the whole frame inverted, words too, for two frames at a time
    if (call?.invert) { B.xfill(0, 0, SW, SH); words.flip?.(); }

    // desktop: a window over the jack's corner hides it, so it never sits on top of the window
    const jack = !P && doc.getElementById?.('jack');
    if (jack) {
      const [jx, jy, jw, jh] = JACK_DESK, under = framesDrawn.some(([x, y, w, h]) => x < jx + jw && x + w > jx && y < jy + jh && y + h > jy);
      const vis = under ? 'hidden' : '';
      if (jack.style.visibility !== vis) jack.style.visibility = vis;
    }
    settleHits();
    firstDraw = false;
    // level 2 night colour (night.js): the same frame, its door and glass shown from the Aseprite night door (the lamp's frame, or the street)
    const ns = night ? nightSheet() : null;
    const usual = out => (ns ? blitNight(B, out, ns, plateFrame(gs.lamp ?? 1, !!gs.ground), { plates: !call?.invert }) : B.blit(out));
    // the glitch (toy.js): the same frame in toy town, torn or faded in over the usual one; nothing moves
    if (glitch?.level > 0) {
      if (base?.length !== d32.length) { base = new Uint32Array(d32.length); toyOut = new Uint32Array(d32.length); }
      usual(base);
      // Open and Keep shut are coloured only while they show; a wrong Open's scream and stamp keep the one-bit glass
      const btn = id => (st.phase === 'round' && !$(id)?.hidden ? lastPlaced.get($(id)) : null);
      const ts = toySheet();
      blitToy(B, toyOut, ts ? toyPicker(ts, glitch.variant, toyFrame(ts, now, !!glitch.talking)) : null, { door: [DX, DY], glass: [GX, GY, GW, GH], windows: framesDrawn, open: btn('open'), shut: btn('shut'),
        menuH: (P ? PHONE.menuH : 17) + 1, floorY: DY + DH, raw: !!(call?.scream || call?.flood || call?.art) });
      mixGlitch(d32, base, toyOut, SW, SH, glitch.level, { now: glitch.now, reduced: glitch.reduced });
    } else usual(d32);
    g.putImageData(img, 0, 0);
  }
  // { level: 0..1, variant, now, reduced } while a glitch shows, else null (ui.js sets it each frame)
  let glitch = null, base = null, toyOut = null;

  return { draw, resize, tall, phone, size: () => [SW, SH], W, H, lastPlaced: () => lastPlaced, phoneFrame: () => (P ? PF : null),
    setNight: on => { night = !!on; }, night: () => night && !!nightSheet(), setGlitch: v => { glitch = v; } };
}
